// API Service with JWT management and offline support
import {
  addToQueue, QueuedRequest,
  zosCachePut, zosCacheGet, zosOutboxPut, zosOutboxAll, zosOutboxRemove, zosOutboxByLead, zosOutboxCount,
} from "./db";
import { toast } from "sonner";
import type {
  PcProject, PcEmployee, PcExtractResponse, PcReportPayload, PcReportSaveResponse,
  PcEmployeeRates, PcControllingProjekt, PcProjektDetail, PcKalkulationPayload, PcNachtrag,
  PcVorgangTreffer, PcVorgangDaten, PcMeinBericht,
  PcFeedbackEintrag, PcFeedbackExtractResponse, PcFeedbackSaveResponse, PcMeineAufgabe, PcFeedbackAufnahme, PcBeobachtungen, PcMeldeUebersicht,
  PcProjektAufgabe,
  PcBueroEintrag, PcBueroExtractResponse, PcBueroSaveResponse, PcBueroReport,
  MeineRechte, ZugangMatrix, ZugangBereich,
} from "@/types";

/** Netzwerkfehler (offline, DNS, Timeout) vs. Backend-Fehler (4xx/5xx/Validation) unterscheiden.
 *  Nur Netzwerkfehler dürfen in Offline-Cache/Outbox-Fallbacks laufen — ein Backend-Fehler
 *  würde sonst beim Sync ewig wiederholt. */
function isNetworkError(err: unknown): boolean {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  if (err instanceof TypeError) return true;  // fetch wirft TypeError bei Netzwerkfehlern
  const msg = err instanceof Error ? err.message : '';
  return msg.includes('Failed to fetch') || msg.includes('NetworkError') || msg.includes('Load failed');
}

/** Zentraler Login-Redirect: merkt sich die aktuelle Seite, damit der User nach
 *  erneutem Login dort weitermachen kann (statt hart auf /home zu landen). */
export function redirectToLogin(): void {
  const here = window.location.pathname + window.location.search;
  const suffix = here && here !== '/login' && here !== '/' ? `?redirect=${encodeURIComponent(here)}` : '';
  window.location.href = `/login${suffix}`;
}

/** Restlaufzeit des JWT in Millisekunden (negativ = abgelaufen, Infinity = kein exp-Claim). */
export function getTokenRemainingMs(token: string): number {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (!payload.exp) return Number.POSITIVE_INFINITY;
    return payload.exp * 1000 - Date.now();
  } catch {
    return 0;
  }
}

const API_BASE = import.meta.env.VITE_API_BASE_URL || "https://kundenservice.ludwig-zimmermann.de/n8n/webhook";
const TOKEN_KEY = "shk_app_token";

// Types
export interface LoginResponse {
  success: boolean;
  token?: string;
  // Backend (Login-Workflow) liefert: { id, email, vorname, nachname, role }.
  user?: {
    id: string;
    email?: string;
    vorname?: string;
    nachname?: string;
    name?: string;
    username?: string;
    role: string;
  };
  error?: string;
}

export interface Customer {
  uuid: string;
  name: string;
  vorname: string;
  kundennummer: string;
  strasse: string;
  plz: string;
  ort: string;
}

export interface ObjectData {
  uuid: string;
  name: string;
  strasse: string;
  plz: string;
  ort: string;
}

export interface Project {
  uuid: string;
  name: string;
  adresse: string;
  status: string;
}

export interface TranscribeResponse {
  raw_text: string;
  professionalized_text: string;
}

export interface SaveReportResponse {
  success: boolean;
  report_id?: string;
  id?: string; // alternative field name for report ID
  pdf_url?: string;
  error?: string;
}

export interface DraftSaveResponse {
  success: boolean;
  id: string;
  error?: string;
}

export interface LeckortungSubmitData {
  customer_uuid: string;
  customer_name: string;
  customer_vorname: string;
  customer_strasse: string;
  customer_plz: string;
  customer_ort: string;
  customer_kundennummer: string;

  object_uuid: string;
  object_name: string;
  object_strasse: string;
  object_plz: string;
  object_ort: string;

  zusatzinfo_wohnungen: string;
  messgeraete: string[];
  schadenbild_raw_text: string;
  schadenbild_professionalized_text: string;
  schadensbeschreibung_raw_text: string;
  schadensbeschreibung_professionalized_text: string;
  druckpruefung: "ja" | "nein" | "nicht_erforderlich";
  leitungen_betroffen: string;
  gewerke: string[];
  gewerke_sonstige_text?: string;
  
  trocknung_erforderlich: "nein" | "daemmschichttrocknung" | "kondenstrocknung" | "beide" | "Dämmschichttrocknung & Kondenstrocknung";
  schimmelsanierung_erforderlich: "nein" | "weniger_1qm" | "mehr_1qm";
  report_date?: string;

  images: ({
    data: string;
    description: string;
  } | {
    remotePath: string;
    description: string;
  })[];
}

// Token management
export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function removeToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
}

// Generate unique ID for queued requests
function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// Main fetch wrapper with auth and offline support
async function apiFetch<T>(endpoint: string, options: RequestInit = {}, allowQueue = true): Promise<T> {
  const url = `${API_BASE}${endpoint}`;
  const token = getToken();

  // Check token expiration
  if (token && isTokenExpired(token)) {
    removeToken();
    redirectToLogin();
    throw new Error("Token abgelaufen");
  }

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const fetchOptions: RequestInit = {
    ...options,
    headers,
  };

  // Check if online
  if (!navigator.onLine && allowQueue) {
    const queuedRequest: QueuedRequest = {
      id: generateId(),
      url,
      method: options.method || "GET",
      headers,
      body: options.body as string | null,
      retries: 0,
      createdAt: Date.now(),
    };

    await addToQueue(queuedRequest);
    toast.info("Offline - Anfrage wird gespeichert");
    throw new Error("OFFLINE_QUEUED");
  }

  try {
    const response = await fetch(url, fetchOptions);

    // 401 und 403 sind NICHT dasselbe:
    //   401 = "wer bist du?"          -> Token weg, neu anmelden hilft.
    //   403 = "du darfst das nicht"   -> Token ist gültig, neu anmelden hilft
    //         nicht und führt in eine Schleife: abmelden, anmelden, wieder
    //         auf dieselbe Seite, wieder raus. Genau das ist am 22.08. bei den
    //         Vollkostensätzen passiert.
    if (response.status === 401) {
      removeToken();
      redirectToLogin();
      throw new Error("Nicht autorisiert");
    }

    if (response.status === 403) {
      const err = new Error("FORBIDDEN") as Error & { status?: number };
      err.status = 403;
      throw err;
    }

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      let errorData: { error?: string } = {};
      try { errorData = errorText ? JSON.parse(errorText) : {}; } catch { /* leerer/non-JSON-Body */ }
      throw new Error(errorData.error || `HTTP ${response.status}${errorText ? ': ' + errorText.slice(0, 100) : ''}`);
    }

    // Defensive parsing: n8n-Webhooks antworten manchmal mit leerem Body bei Success.
    // Wir wollen nicht crashen mit "Unexpected end of JSON input".
    const text = await response.text();
    if (!text || text.trim().length === 0) {
      return {} as T;
    }
    try {
      return JSON.parse(text) as T;
    } catch (parseErr) {
      console.warn(`apiFetch: konnte Response von ${endpoint} nicht als JSON parsen — gebe Roh-Text zurück`, parseErr);
      return { _raw: text } as unknown as T;
    }
  } catch (error) {
    if (error instanceof Error && error.message === "OFFLINE_QUEUED") {
      throw error;
    }

    // Network error - queue if allowed
    if (allowQueue && options.method && options.method !== "GET") {
      const queuedRequest: QueuedRequest = {
        id: generateId(),
        url,
        method: options.method,
        headers,
        body: options.body as string | null,
        retries: 0,
        createdAt: Date.now(),
      };

      await addToQueue(queuedRequest);
      toast.info("Verbindungsfehler - Anfrage wird gespeichert");
      throw new Error("OFFLINE_QUEUED");
    }

    throw error;
  }
}

// Auth endpoints - POST /auth/login
export async function login(username: string, password: string): Promise<LoginResponse> {
  const response = await apiFetch<LoginResponse>(
    "/auth/login",
    {
      method: "POST",
      body: JSON.stringify({ username, password }),
    },
    false,
  );

  if (response.success && response.token) {
    setToken(response.token);
  }

  return response;
}

export function logout(): void {
  removeToken();
  localStorage.removeItem("shk_app_user");
}

// Raw API response type (what the backend actually returns)
interface CustomerApiResponse {
  uuid: string;
  firma: string;
  vorname: string;
  nachname: string;
  adresse: string;
  plz: string;
  ort: string;
  kundenRolle?: {
    kundennummer: string;
  };
}

// Customer endpoints - GET /customers?search=SUCHBEGRIFF
export async function searchCustomers(query: string = ""): Promise<Customer[]> {
  const searchParam = query ? `?search=${encodeURIComponent(query)}` : '';
  console.log('🔍 Kundensuche - Anfrage an:', `/customers${searchParam}`);
  
  const result = await apiFetch<CustomerApiResponse[]>(
    `/customers${searchParam}`,
    {
      method: "GET",
    },
    false,
  );
  
  console.log('✅ Kundensuche - Ergebnis:', result);
  
  // Map API response to Customer interface
  // Use nachname as name, kundennummer from nested kundenRolle
  return result.map((item) => ({
    uuid: item.uuid,
    name: item.nachname || item.firma,
    vorname: item.vorname || '',
    kundennummer: item.kundenRolle?.kundennummer || '',
    strasse: item.adresse,
    plz: item.plz,
    ort: item.ort,
  }));
}

// Object endpoints - GET /webhook/objects?search=STRASSE
export async function searchObjects(streetSearch: string): Promise<ObjectData[]> {
  console.log('🏠 Objektsuche - Anfrage an:', `/webhook/objects?search=${streetSearch}`);
  
  const result = await apiFetch<ObjectData[]>(
    `/webhook/objects?search=${encodeURIComponent(streetSearch)}`,
    {},
    false,
  );
  
  console.log('✅ Objektsuche - Ergebnis:', result);
  return result;
}

// Extract street from address (helper function)
export function extractStreetFromAddress(adresse: string): string {
  // Address format: "Musterstraße 1" or "Musterstraße 1, 52070 Aachen"
  const parts = adresse.split(",");
  return parts[0].trim();
}

// Project endpoints
export async function getProjects(): Promise<Project[]> {
  return apiFetch<Project[]>("/projects", {}, false);
}

export async function searchProjects(query: string): Promise<Project[]> {
  return apiFetch<Project[]>(`/projects?search=${encodeURIComponent(query)}`, {}, false);
}

// Sprachfunktionen (Transkription/Optimierung) brauchen zwingend das Backend-AI.
// Offline können sie nicht in die Queue (FormData/Response-abhängig) — daher klare
// Meldung statt eines verwirrenden "Transkription fehlgeschlagen".
function assertVoiceOnline(): void {
  if (!navigator.onLine) {
    throw new Error("Sprachfunktion benötigt eine Internetverbindung");
  }
}

// Audio Transcription - POST /leckortung/audio
// Returns both raw_text and professionalized_text
export async function transcribeAudio(audioBlob: Blob): Promise<TranscribeResponse> {
  assertVoiceOnline();
  const formData = new FormData();
  formData.append("audio", audioBlob, "recording.webm");

  const token = getToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}/leckortung/audio`, {
    method: "POST",
    headers,
    body: formData,
  });

  if (!response.ok) {
    throw new Error("Transkription fehlgeschlagen");
  }

  return response.json();
}

// Re-optimize edited raw text - POST /leckortung/optimize
export async function optimizeText(rawText: string): Promise<{ professionalized_text: string }> {
  const token = getToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}/leckortung/optimize`, {
    method: "POST",
    headers,
    body: JSON.stringify({ raw_text: rawText }),
  });

  if (!response.ok) {
    throw new Error("Text-Optimierung fehlgeschlagen");
  }

  return response.json();
}

// Simple transcription for image descriptions and other fields - POST /leckortung/transcribe-simple
export async function transcribeSimple(audioBlob: Blob): Promise<{ text: string }> {
  assertVoiceOnline();
  const formData = new FormData();
  formData.append("audio", audioBlob, "recording.webm");

  const token = getToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}/leckortung/transcribe-simple`, {
    method: "POST",
    headers,
    body: formData,
  });

  if (!response.ok) {
    throw new Error("Transkription fehlgeschlagen");
  }

  return response.json();
}

// Transcribe Zusatzinfo Wohnungen - POST /leckortung/transcribe-wohnungen
export async function transcribeZusatzinfoWohnungen(audioBlob: Blob): Promise<{ text: string }> {
  assertVoiceOnline();
  const formData = new FormData();
  formData.append("audio", audioBlob, "recording.webm");

  const token = getToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}/leckortung/transcribe-wohnungen`, {
    method: "POST",
    headers,
    body: formData,
  });

  if (!response.ok) {
    throw new Error("Transkription fehlgeschlagen");
  }

  return response.json();
}

// Transcribe Schadensbeschreibung - POST /leckortung/transcribe-schaden
export async function transcribeSchadensbeschreibung(audioBlob: Blob): Promise<{ raw_text: string; professionalized_text: string }> {
  assertVoiceOnline();
  const formData = new FormData();
  formData.append("audio", audioBlob, "recording.webm");

  const token = getToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}/leckortung/transcribe-schaden`, {
    method: "POST",
    headers,
    body: formData,
  });

  if (!response.ok) {
    throw new Error("Transkription fehlgeschlagen");
  }

  return response.json();
}

// Re-optimize Schadensbeschreibung - POST /leckortung/optimize2
export async function optimizeSchadensbeschreibung(rawText: string): Promise<{ professionalized_text: string }> {
  const token = getToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}/leckortung/optimize2`, {
    method: "POST",
    headers,
    body: JSON.stringify({ raw_text: rawText }),
  });

  if (!response.ok) {
    throw new Error("Text-Optimierung fehlgeschlagen");
  }

  return response.json();
}

// Save Leckortung Draft - POST to /leckortung/save
// If id is null, creates new report (INSERT) and returns id
// If id is set, updates existing report (UPDATE)
export async function saveLeckortungDraft(data: { id: string | null } & LeckortungSubmitData & { object_image?: { data: string; description: string } | { remotePath: string; description: string } | null }): Promise<DraftSaveResponse> {
  const token = getToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  console.log('💾 Saving draft, id:', data.id || 'NEW');

  const response = await fetch("https://kundenservice.ludwig-zimmermann.de/n8n/webhook/leckortung/save", {
    method: "POST",
    headers,
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    console.error('Draft save failed:', response.status, errorText);
    throw new Error("Speichern fehlgeschlagen");
  }

  // Defensive parsing: der n8n-Flow kann bei einem (Teil-)Fehler mit leerem
  // 200-Body antworten. response.json() würde dann mit "Unexpected end of JSON
  // input" crashen.
  const text = await response.text();
  let result: { id?: string; report_id?: string } = {};
  if (text && text.trim().length > 0) {
    try {
      result = JSON.parse(text);
    } catch {
      console.warn('Draft save: Response war kein JSON:', text.slice(0, 200));
    }
  }
  console.log('✅ Draft saved, response:', result);

  const savedId = result.id || result.report_id || data.id || '';
  // Ohne Bericht-ID ist der Save real fehlgeschlagen (z.B. der Flow brach vor
  // dem INSERT ab). NICHT still Erfolg melden — sonst bleibt formData.id null
  // und der nächste Save erzeugt einen weiteren INSERT (Duplikat).
  if (!savedId) {
    throw new Error('Speichern fehlgeschlagen: keine Bericht-ID vom Server erhalten');
  }

  return {
    success: true,
    id: savedId,
  };
}

// Save Leckortung Report - POST to webhook/leckortung/save (legacy, kept for compatibility)
export async function saveLeckortungReport(data: LeckortungSubmitData & { object_image?: { data: string; description: string } | null }): Promise<SaveReportResponse> {
  const token = getToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch("https://kundenservice.ludwig-zimmermann.de/n8n/webhook/leckortung/save", {
    method: "POST",
    headers,
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error("Speichern fehlgeschlagen");
  }

  return response.json();
}

// Submit Leckortung Report - POST to /leckortung/submit
export async function submitLeckortungReport(data: LeckortungSubmitData & { id?: string | null; signature?: string | null; object_image?: { data: string; description: string } | { remotePath: string; description: string } | null }): Promise<SaveReportResponse> {
  const token = getToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  console.log('📤 Submitting report, id:', data.id || 'UNKNOWN');

  const url = "https://kundenservice.ludwig-zimmermann.de/n8n/webhook/leckortung/submit";
  const body = JSON.stringify(data);

  // Offline → in die Offline-Queue legen statt den Bericht zu verlieren.
  // Der Body ist reines JSON, also queue-bar; processQueue() sendet ihn
  // später mit frischem Token erneut. handleSubmit fängt OFFLINE_QUEUED ab.
  if (!navigator.onLine) {
    await addToQueue({ id: generateId(), url, method: "POST", headers, body, retries: 0, createdAt: Date.now() });
    throw new Error("OFFLINE_QUEUED");
  }

  let response: Response;
  try {
    response = await fetch(url, { method: "POST", headers, body });
  } catch (networkErr) {
    // Verbindungsfehler → ebenfalls queuen, nicht verwerfen.
    console.error('Submit network error, queuing:', networkErr);
    await addToQueue({ id: generateId(), url, method: "POST", headers, body, retries: 0, createdAt: Date.now() });
    throw new Error("OFFLINE_QUEUED");
  }

  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    console.error('Submit failed:', response.status, errorText);
    throw new Error("Absenden fehlgeschlagen");
  }

  // Defensive parse (n8n kann mit leerem Body antworten).
  const text = await response.text();
  if (!text || !text.trim()) return { success: true };
  try {
    return JSON.parse(text);
  } catch {
    console.warn('Submit: Response war kein JSON:', text.slice(0, 200));
    return { success: true };
  }
}

// Tagesbericht Report
export async function saveTagesberichtReport(data: Record<string, unknown>): Promise<SaveReportResponse> {
  return apiFetch<SaveReportResponse>("/reports/tagesbericht", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function getMyReports(): Promise<Record<string, unknown>[]> {
  return apiFetch<Record<string, unknown>[]>("/reports/list", {}, false);
}

// Leckortung Reports List - GET /leckortung/list
export interface LeckortungListItem {
  id: string;
  status: 'draft' | 'completed' | string;

  customer_firma: string;
  customer_strasse?: string | null;
  customer_plz?: string | null;
  customer_ort?: string | null;

  object_name?: string | null;
  object_strasse?: string | null;
  object_plz?: string | null;
  object_ort?: string | null;

  pdf_filename?: string | null;

  report_date?: string | null;
  techniker_vorname?: string | null;
  techniker_nachname?: string | null;

  created_at: string;
  updated_at?: string | null;
}

export async function getLeckortungReports(): Promise<LeckortungListItem[]> {
  const token = getToken();

  // Handle expired tokens early (same behavior as apiFetch)
  if (token && isTokenExpired(token)) {
    removeToken();
    redirectToLogin();
    throw new Error("Token abgelaufen");
  }

  const urls = [
    `${API_BASE}/leckortung/list?_=${Date.now()}`,
  ];

  let lastError: unknown;

  for (const url of urls) {
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        cache: "no-store",
      });

      if (response.status === 401 || response.status === 403) {
        removeToken();
        redirectToLogin();
        throw new Error("Nicht autorisiert");
      }

      if (!response.ok) {
        const text = await response.text().catch(() => "");
        throw new Error(`HTTP ${response.status} ${response.statusText}${text ? `: ${text}` : ""}`);
      }

      const json = await response.json();

      // Supported shapes:
      // 1) [ { ... }, ... ]
      // 2) { success: true, reports: [ ... ] }
      // 3) { success: true, reports: { ... } }
      if (Array.isArray(json)) return json as LeckortungListItem[];
      if (json && typeof json === 'object' && 'reports' in (json as Record<string, unknown>)) {
        const reports = (json as { reports?: unknown }).reports;
        if (Array.isArray(reports)) return reports as LeckortungListItem[];
        if (reports && typeof reports === 'object') return [reports as LeckortungListItem];
        return [];
      }

      throw new Error('Unerwartetes Response-Format');
    } catch (err) {
      // If we already triggered an auth redirect, don't wrap the error.
      if (err instanceof Error && (err.message === "Nicht autorisiert" || err.message === "Token abgelaufen")) {
        throw err;
      }
      lastError = err;
    }
  }

  const details = lastError instanceof Error ? lastError.message : String(lastError);
  throw new Error(`Berichte konnten nicht geladen werden (${details})`);
}

// Server-seitige Bilddaten vom Backend
export interface ServerImageData {
  path: string;       // z.B. "leckortung/images/..._0.jpg"
  role: 'object' | 'additional';
  index: number;
  description: string;
}

// Image-Webhook URL Builder — IDs via ENV überschreibbar (.env.example), Fallback = Produktion
const IMAGE_WEBHOOK_BASE = import.meta.env.VITE_IMAGE_WEBHOOK_BASE || API_BASE;
const IMAGE_WEBHOOK_ID = import.meta.env.VITE_IMAGE_WEBHOOK_ID || "cdecb408-8e30-49f3-9c5b-70382c99d515";

export function buildImageUrl(reportId: string, filePath: string, token: string): string {
  const filename = filePath.split("/").pop() || "";
  return `${IMAGE_WEBHOOK_BASE}/${IMAGE_WEBHOOK_ID}/leckortung/${encodeURIComponent(reportId)}/image/${encodeURIComponent(filename)}?token=${encodeURIComponent(token)}`;
}

// Leckortung Report Detail - GET /leckortung-get/leckortung/:id
export interface LeckortungDetailItem extends LeckortungListItem {
  // Additional fields that might come from detail endpoint
  customer_uuid?: string | null;
  customer_kundennummer?: string | null;
  object_uuid?: string | null;
  zusatzinfo_wohnungen?: string | null;
  messgeraete?: string[];
  schadenbild_raw_text?: string | null;
  schadenbild_professionalized_text?: string | null;
  schadensbeschreibung?: string | null;
  schadensbeschreibung_raw_text?: string | null;
  schadensbeschreibung_professionalized_text?: string | null;
  druckpruefung?: string | null;
  leitungen_betroffen?: string | null;
  gewerke?: string[];
  trocknung_erforderlich?: string | null;
  schimmelsanierung_erforderlich?: string | null;
  signature_base64?: string | null;
  images?: ServerImageData[];
  download_token?: string | null;
}

export async function getLeckortungReportById(id: string): Promise<LeckortungDetailItem> {
  const token = getToken();
  const cacheBust = Date.now();

  // n8n runs under /n8n/webhook, use API_BASE which points there
  const url = `${API_BASE}/leckortung-get/leckortung/${id}?_=${cacheBust}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    cache: "no-store",
  });

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(`HTTP ${response.status} ${response.statusText}${text ? `: ${text}` : ""}`);
  }

  const json = await response.json();

  // Handle { success: true, report: { ... } } or { success: true, reports: { ... } } or direct object
  if (json && typeof json === 'object') {
    const anyJson = json as Record<string, unknown>;
    if (anyJson.report && typeof anyJson.report === 'object') return anyJson.report as unknown as LeckortungDetailItem;
    if (anyJson.reports && typeof anyJson.reports === 'object') return anyJson.reports as unknown as LeckortungDetailItem;
    if ('id' in anyJson) return anyJson as unknown as LeckortungDetailItem;
  }

  throw new Error('Unerwartetes Response-Format');
}

// Dashboard endpoint
export async function getDashboardData(): Promise<Record<string, unknown>> {
  return apiFetch<Record<string, unknown>>("/dashboard", {}, false);
}

// Convert File to Base64
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(",")[1];
      resolve(base64);
    };
    reader.onerror = reject;
  });
}

// =============================================================
// ZOS — Lead-Management Endpoints
// =============================================================

export type ZosLeadStatus =
  | 'NEW' | 'WAITING_FOR_CUSTOMER' | 'PREQUALIFICATION' | 'NEEDS_REVIEW'
  | 'APPOINTMENT_REQUIRED' | 'APPOINTMENT_SET'
  | 'AUDIT_PENDING' | 'AUDIT_DONE'   // V5 — Monteur-Erfassungs-Phase
  | 'OFFER_PENDING' | 'OFFER_SENT'
  | 'FOLLOWUP' | 'NEGOTIATION' | 'WON' | 'LOST' | 'ARCHIVED';

export type ZosSlaStatus = 'green' | 'yellow' | 'red';

/** Operator-Sprache für Lead-Status (KONZEPT-LEAD-PIPELINE.md).
 *  Single source of truth — Komponenten dürfen kein eigenes STATUS_LABEL haben. */
export const STATUS_LABEL: Record<ZosLeadStatus, string> = {
  NEW: 'Neu im Eingang',
  WAITING_FOR_CUSTOMER: 'Warte auf Rückmeldung Kunde',
  PREQUALIFICATION: 'In Qualifizierung',
  NEEDS_REVIEW: 'Chef-Prüfung nötig',
  APPOINTMENT_REQUIRED: 'Termin offen',
  APPOINTMENT_SET: 'Einsicht verplant',
  AUDIT_PENDING: 'Einsicht anstehend',
  AUDIT_DONE: 'Einsicht erledigt',
  OFFER_PENDING: 'Angebot vorbereiten',
  OFFER_SENT: 'Angebot beim Kunden',
  FOLLOWUP: 'Nachfass läuft',
  NEGOTIATION: 'In Verhandlung',
  WON: 'Auftrag erteilt',
  LOST: 'Kein Auftrag',
  ARCHIVED: 'Archiviert',
};

/** Tailwind-Klassen für Status-Badge. */
export const STATUS_BADGE: Record<ZosLeadStatus, string> = {
  NEW: 'bg-blue-100 text-blue-800 border-blue-200',
  WAITING_FOR_CUSTOMER: 'bg-amber-100 text-amber-800 border-amber-200',
  PREQUALIFICATION: 'bg-orange-100 text-orange-800 border-orange-200',
  NEEDS_REVIEW: 'bg-orange-100 text-orange-800 border-orange-200',
  APPOINTMENT_REQUIRED: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  APPOINTMENT_SET: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  AUDIT_PENDING: 'bg-cyan-100 text-cyan-800 border-cyan-200',
  AUDIT_DONE: 'bg-teal-100 text-teal-800 border-teal-200',
  OFFER_PENDING: 'bg-purple-100 text-purple-800 border-purple-200',
  OFFER_SENT: 'bg-purple-100 text-purple-800 border-purple-200',
  FOLLOWUP: 'bg-amber-100 text-amber-800 border-amber-200',
  NEGOTIATION: 'bg-amber-100 text-amber-800 border-amber-200',
  WON: 'bg-green-100 text-green-800 border-green-200',
  LOST: 'bg-red-100 text-red-800 border-red-200',
  ARCHIVED: 'bg-gray-100 text-gray-700 border-gray-200',
};

// ---------- LEAD-PHASE (KONZEPT-LEAD-PIPELINE.md V2 — 4 Phasen) ----------

/** Die 4 Phasen der Lead-Pipeline. ENUM-Codes bleiben für Datenmodell,
 *  Phasen sind das User-mentale-Modell darüber.
 *  V10c-Anpassung: ehemals EINGANG (NEW/WAITING) und QUALIFIZIERUNG zusammengeführt
 *  zu einer Phase QUALIFIZIERUNG — aus Operator-Sicht ist das ein einziger Arbeitsschritt. */
export type LeadPhase = 'QUALIFIZIERUNG' | 'EINSICHT' | 'ANGEBOT' | 'ABSCHLUSS';

export const LEAD_PHASES: ReadonlyArray<LeadPhase> = ['QUALIFIZIERUNG','EINSICHT','ANGEBOT','ABSCHLUSS'];

export const PHASE_LABEL: Record<LeadPhase, string> = {
  QUALIFIZIERUNG: 'Qualifizierung',
  EINSICHT: 'Vor-Ort-Einsicht',
  ANGEBOT: 'Angebot',
  ABSCHLUSS: 'Abschluss',
};

export const PHASE_NUMBER: Record<LeadPhase, number> = {
  QUALIFIZIERUNG: 1, EINSICHT: 2, ANGEBOT: 3, ABSCHLUSS: 4,
};

/** Tailwind-Klassen für Phase-Badge (Single Source). */
export const PHASE_BADGE: Record<LeadPhase, string> = {
  QUALIFIZIERUNG: 'bg-blue-50 text-blue-700 border-blue-200',
  EINSICHT:       'bg-cyan-50 text-cyan-700 border-cyan-200',
  ANGEBOT:        'bg-purple-50 text-purple-700 border-purple-200',
  ABSCHLUSS:      'bg-gray-100 text-gray-700 border-gray-300',
};

/** Status → Phase Mapping (Hauptregel des Konzepts). */
const STATUS_TO_PHASE: Record<ZosLeadStatus, LeadPhase> = {
  NEW: 'QUALIFIZIERUNG',
  WAITING_FOR_CUSTOMER: 'QUALIFIZIERUNG',
  PREQUALIFICATION: 'QUALIFIZIERUNG',
  NEEDS_REVIEW: 'QUALIFIZIERUNG',
  APPOINTMENT_REQUIRED: 'EINSICHT',
  APPOINTMENT_SET: 'EINSICHT',
  AUDIT_PENDING: 'EINSICHT',
  AUDIT_DONE: 'EINSICHT',
  OFFER_PENDING: 'ANGEBOT',
  OFFER_SENT: 'ANGEBOT',
  FOLLOWUP: 'ANGEBOT',
  NEGOTIATION: 'ANGEBOT',
  WON: 'ABSCHLUSS',
  LOST: 'ABSCHLUSS',
  ARCHIVED: 'ABSCHLUSS',
};

/** Gibt die Phase eines Leads zurück. */
export function getLeadPhase(status: ZosLeadStatus): LeadPhase {
  return STATUS_TO_PHASE[status];
}

/** Liefert "Phase 1 von 4" als String für Anzeige. */
export function formatPhaseIndicator(status: ZosLeadStatus): string {
  const phase = getLeadPhase(status);
  return `Phase ${PHASE_NUMBER[phase]} von 4 · ${PHASE_LABEL[phase]}`;
}

/** Tailwind-Klassen für SLA-Ampel. */
export const SLA_COLOR: Record<ZosSlaStatus, string> = {
  green: 'bg-green-500',
  yellow: 'bg-yellow-500',
  red: 'bg-red-500',
};

/** Klartext für SLA-Status. */
export const SLA_LABEL: Record<ZosSlaStatus, string> = {
  green: 'OK',
  yellow: 'Bald fällig',
  red: 'Überfällig',
};

/** Priorität-Badge (A=sofort, D=Bei-Kapazität). */
export const PRIORITY_BADGE: Record<'A'|'B'|'C'|'D', string> = {
  A: 'bg-red-600 text-white',
  B: 'bg-orange-500 text-white',
  C: 'bg-yellow-500 text-white',
  D: 'bg-gray-400 text-white',
};

/** Lead-Typ V2 (Eingangskategorie — was am Anfang schnell erkennbar ist).
 *  Komplexität, Beratungsmodus, Standardfall etc. ergeben sich erst nach Qualifizierung. */
export type ZosLeadType =
  | 'HEATPUMP'              // Wärmepumpe
  | 'HEATING_CONVENTIONAL'  // Gas, Öl, Hybrid, klassisch
  | 'AIR_CONDITIONING'      // Klimaanlage — Neueinbau. Service läuft über den Kundendienst, nicht als Lead.
  | 'BATHROOM'              // Bad — Standard/Komplex via complexity_level
  | 'APARTMENT_RENOVATION'  // Sanierung Wohnung
  | 'HOUSE_RENOVATION_EFH'  // Sanierung EFH
  | 'SERVICE_NEW_CUSTOMER'  // Kundendienst Neukunde
  | 'NEW_BUILD'             // Neubau — meist ablehnen
  // Legacy V1 (in DB noch erlaubt, in UI ausgeblendet, kein Mapping mehr nötig):
  | 'WP_STANDARD' | 'HEIZUNG_STANDARD' | 'KD_BESTAND' | 'KD_NEUKUNDE'
  | 'BAD_STANDARD' | 'BAD_KOMPLEX' | 'BERATUNG' | 'SONDERFALL';

/** Anzeige-Labels und Reihenfolge für Lead-Typ-Dropdowns. Single source of truth. */
export const LEAD_TYPE_OPTIONS: ReadonlyArray<{ value: ZosLeadType; label: string }> = [
  { value: 'HEATPUMP',              label: 'Wärmepumpe' },
  { value: 'HEATING_CONVENTIONAL',  label: 'Heizung konventionell' },
  { value: 'AIR_CONDITIONING',      label: 'Klimaanlage' },
  { value: 'BATHROOM',              label: 'Bad' },
  { value: 'APARTMENT_RENOVATION',  label: 'Sanierung Wohnung' },
  { value: 'HOUSE_RENOVATION_EFH',  label: 'Sanierung EFH' },
  { value: 'SERVICE_NEW_CUSTOMER',  label: 'Kundendienst (Neukunde)' },
  { value: 'NEW_BUILD',             label: 'Neubau' },
];

/** Lookup nach ENUM-Wert auf Anzeige-Label. Enthält Fallbacks für Legacy-Werte. */
export const LEAD_TYPE_LABEL: Record<ZosLeadType, string> = {
  HEATPUMP:              'Wärmepumpe',
  HEATING_CONVENTIONAL:  'Heizung konventionell',
  AIR_CONDITIONING:      'Klimaanlage',
  BATHROOM:              'Bad',
  APARTMENT_RENOVATION:  'Sanierung Wohnung',
  HOUSE_RENOVATION_EFH:  'Sanierung EFH',
  SERVICE_NEW_CUSTOMER:  'Kundendienst (Neukunde)',
  NEW_BUILD:             'Neubau',
  // Legacy (sollte nicht mehr vorkommen, aber falls doch):
  WP_STANDARD:       'WP Standard (alt)',
  HEIZUNG_STANDARD:  'Heizung Standard (alt)',
  KD_BESTAND:        'Kundendienst Bestand (alt)',
  KD_NEUKUNDE:       'Kundendienst Neu (alt)',
  BAD_STANDARD:      'Bad Standard (alt)',
  BAD_KOMPLEX:       'Bad Komplex (alt)',
  BERATUNG:          'Beratung (alt)',
  SONDERFALL:        'Sonderfall (alt)',
};

// ===== V2 Lead-Dimension-Felder =====

export type ZosComplexityLevel = 'unknown' | 'standard' | 'normal' | 'complex';
export const COMPLEXITY_LABEL: Record<ZosComplexityLevel, string> = {
  unknown: '— unklar',
  standard: 'Standard',
  normal: 'Normal',
  complex: 'Komplex',
};

export type ZosProcessPath =
  | 'qualify'              // noch in Qualifizierung
  | 'request_missing_data' // Unterlagen anfordern
  | 'appointment'          // Vor-Ort-Termin
  | 'offer_preparation'    // Angebot vorbereiten
  | 'paid_consulting'      // bezahlte Beratung
  | 'service_process'      // Serviceprozess (KD Bestand)
  | 'tech_review'          // Technikprüfung
  | 'decline'              // ablehnen
  | 'archive';             // archivieren

export const PROCESS_PATH_LABEL: Record<ZosProcessPath, string> = {
  qualify:              'In Qualifizierung',
  request_missing_data: 'Unterlagen anfordern',
  appointment:          'Vor-Ort-Termin',
  offer_preparation:    'Angebot vorbereiten',
  paid_consulting:      'Bezahlte Beratung',
  service_process:      'Serviceprozess',
  tech_review:          'Technikprüfung',
  decline:              'Ablehnen',
  archive:              'Archivieren',
};

export type ZosCustomerStatus = 'unknown' | 'new_customer' | 'existing_customer' | 'house_management' | 'commercial';
export const CUSTOMER_STATUS_LABEL: Record<ZosCustomerStatus, string> = {
  unknown:           '— unklar',
  new_customer:      'Neukunde',
  existing_customer: 'Bestandskunde',
  house_management:  'Hausverwaltung',
  commercial:        'Gewerbe',
};

export type ZosQualificationResult =
  | 'not_qualified' | 'partially_qualified' | 'qualified'
  | 'appointment_ready' | 'offer_ready' | 'declined' | 'routed_to_service';

export const QUALIFICATION_RESULT_LABEL: Record<ZosQualificationResult, string> = {
  not_qualified:       'Noch nicht qualifiziert',
  partially_qualified: 'Teilweise qualifiziert',
  qualified:           'Qualifiziert',
  appointment_ready:   'Terminbereit',
  offer_ready:         'Angebotsbereit',
  declined:            'Abgelehnt',
  routed_to_service:   'In Serviceprozess',
};

export type ZosPriority = 'A' | 'B' | 'C' | 'D';

export interface ZosScoringFactor {
  key: string;
  label: string;
  impact: number;
  category: string;
}

/** Mögliche Duplikate (Phase H, seit 2026-06-03). Wird aus Activities ('possible_duplicate') ausgelesen. */
export interface ZosPossibleDuplicate {
  lead_id: string;
  lead_number?: string;
  match_type?: 'pds_uuid' | 'phone' | 'email';
  side?: 'new' | 'existing';
  blocked?: boolean;
}

export interface ZosScoringBreakdown {
  positive: ZosScoringFactor[];
  negative: ZosScoringFactor[];
  positive_total: number;
  negative_total: number;
  net_score: number;
}
export interface ZosCompletionItem {
  key: string;
  label: string;
  classification: 'required' | 'recommended' | 'optional';
  done: boolean;
}
export interface ZosActiveRedFlag {
  code: string;
  label: string;
  severity: 'info' | 'warning' | 'critical';
  blocks_appointment: boolean;
  blocks_offer: boolean;
  requires_review: boolean;
  triggers_consulting_mode: boolean;
}

export interface ZosLead {
  id: string;
  lead_number: string;
  created_at: string;
  updated_at: string;
  source: string | null;
  source_reference_id: string | null;
  lead_type: ZosLeadType | null;
  status: ZosLeadStatus;
  score: number | null;
  sla_status: ZosSlaStatus;
  current_handler: string | null;
  handler_role: 'office' | 'tech' | 'management' | 'admin' | null;
  assigned_technician_id: string | null;  // V5: UUID des zugewiesenen Monteurs (n8n-User-Tabelle)
  customer_name: string | null;
  customer_phone: string | null;
  customer_email: string | null;
  pds_customer_id: string | null;
  pds_sync_status: 'matched' | 'pending' | 'created' | 'failed' | 'ignored';
  budget_range?: string | null;
  property_type?: string | null;
  heating_system?: string | null;
  manufacturer?: string | null;
  energy_consultant?: boolean | null;
  heat_load_available?: boolean | null;
  photos_complete?: boolean | null;
  appointment_required?: boolean;
  appointment_date?: string | null;
  offer_required?: boolean;
  offer_sent_at?: string | null;
  next_action: string | null;
  next_action_due: string | null;
  red_flags?: unknown[];
  tags?: unknown[];
  notes?: string | null;
  // Scoring / Vollständigkeit (aus Schema v2/v3)
  priority?: ZosPriority | null;
  completion_score?: number | null;
  completion_breakdown?: ZosCompletionItem[] | null;
  /** Voicery-Anruf-Daten (Phase G, seit 2026-06-03). */
  intake_data?: {
    source?: string;
    customer_name_raw?: string;
    phone?: string;
    prio_voicery?: string;
    status_voicery?: string;
    object_address?: string;
    kurzbeschreibung?: string;
    ai_summary?: string;
    call_time?: string;
    recording_url?: string;
    pds_uuid?: string;
    telegram_chat_id?: string;
    telegram_message_id?: string;
    lead_type_guessed?: string;
    parsed_at?: string;
    raw_html?: string;
    /** Ab ZOS-54: Leads, die aus einem Zammad-Ticket uebernommen wurden. */
    pds_kundennummer?: string | null;
    zammad_ticket_id?: number | string;
    zammad_ticket_number?: string;
    zammad_ticket_title?: string | null;
    zammad_customer_id?: number | string | null;
    zammad_group?: string | null;
    erstkontakt_typ?: string | null;
    erstkontakt_text?: string | null;
    uebernommen_am?: string;
  } | null;
  /** Soft-Delete-Felder (Phase F). */
  deleted_at?: string | null;
  deleted_reason?: string | null;
  deleted_by?: string | null;
  is_appointment_ready?: boolean;
  is_offer_ready?: boolean;
  scoring_breakdown?: ZosScoringBreakdown | null;
  active_red_flags?: ZosActiveRedFlag[];
  killer_active?: boolean;
  // V2 Lead-Dimensionen (Schema v9a)
  complexity_level?: ZosComplexityLevel;
  process_path?: ZosProcessPath;
  customer_status?: ZosCustomerStatus;
  qualification_result?: ZosQualificationResult;
  review_required?: boolean;
  management_override?: boolean;
  // V10f — Audit-Mirror-Pfad: 'phone' (klassisch Operator) | 'walkin' (Spontan-Aufnahme)
  qualification_source?: 'phone' | 'walkin';
  // V2 PDS-Angebot-Brücke (Schema v6)
  pds_offer_id?: string | null;
  pds_offer_status?: 'none' | 'drafting' | 'pds_draft' | 'sent' | 'accepted' | 'rejected' | 'withdrawn';
  pds_offer_synced_at?: string | null;
}

export interface ZosActivity {
  id: string;
  type: 'call' | 'mail' | 'status_change' | 'note' | 'followup' | 'escalation' | 'ai_summary' | 'ai_scoring';
  actor: string;
  payload: Record<string, unknown>;
  created_at: string;
}

export interface ZosLeadsListResponse {
  items: ZosLead[];
  total: number;
  limit: number;
  offset: number;
  filters: { status: string | null; handler: string | null; sla: string | null };
  /** Offline-Fallback (seit 09.06.): true wenn aus IndexedDB-Cache statt Netz. */
  _offline?: boolean;
  _cachedAt?: number;
}

export type ZosArtifactKind = 'photo' | 'video' | 'audio' | 'document' | 'note' | 'sketch';

export interface ZosArtifact {
  id: string;
  kind: ZosArtifactKind;
  storage_path: string;
  mime: string | null;
  bytes_size: number | null;
  description: string | null;
  context: string | null;
  taken_at: string | null;
  uploaded_by: string | null;
  created_at: string;
}

export interface ZosLeadDetailResponse {
  lead: ZosLead;
  activities: ZosActivity[];
  activity_count: number;
  contacts: ZosContactView[];
  addresses: ZosAddressView[];
  artifacts?: ZosArtifact[];
  artifact_count?: number;
  /** Offline-Fallback (seit 09.06.): true wenn aus IndexedDB-Cache statt Netz. */
  _offline?: boolean;
  _cachedAt?: number;
}

export interface ZosLeadsListParams {
  status?: ZosLeadStatus | ZosLeadStatus[];
  handler?: string;
  sla?: ZosSlaStatus;
  assigned_to?: string;     // V5: assigned_technician_id
  limit?: number;
  offset?: number;
}

export async function getZosLeads(params: ZosLeadsListParams = {}): Promise<ZosLeadsListResponse> {
  const query = new URLSearchParams();
  if (params.status) {
    query.set('status', Array.isArray(params.status) ? params.status.join('|') : params.status);
  }
  if (params.handler) query.set('handler', params.handler);
  if (params.sla) query.set('sla', params.sla);
  if (params.assigned_to) query.set('assigned_to', params.assigned_to);
  if (params.limit != null) query.set('limit', String(params.limit));
  if (params.offset != null) query.set('offset', String(params.offset));
  const suffix = query.toString();
  const cacheKey = `leads:${suffix}`;
  try {
    const r = await apiFetch<ZosLeadsListResponse>(`/zos/leads${suffix ? '?' + suffix : ''}`, {}, false);
    void zosCachePut(cacheKey, r).catch(() => {/* Cache-Fehler nie fatal */});
    return r;
  } catch (err) {
    if (!isNetworkError(err)) throw err;
    const cached = await zosCacheGet(cacheKey).catch(() => undefined);
    if (!cached) throw err;
    return { ...(cached.data as ZosLeadsListResponse), _offline: true, _cachedAt: cached.savedAt };
  }
}

// =============================================================
// V5: Dashboard-Stats + User-List (für Monteur-Zuweisung)
// =============================================================

export type DashboardBucketKey =
  | 'new_today'
  | 'awaiting_assignment'
  | 'audits_due'
  | 'audit_done'
  | 'offer_due'
  | 'offer_sent'
  | 'overdue'
  | 'lost_week';

export interface DashboardBucketSample {
  id: string;
  lead_number: string;
  customer_name: string | null;
  status: ZosLeadStatus;
  priority: 'A'|'B'|'C'|'D' | null;
  sla_status: ZosSlaStatus | null;
}

export interface DashboardBucket {
  count: number;
  samples: DashboardBucketSample[];
}

export interface DashboardStatsResponse {
  ok: boolean;
  generated_at: string;
  buckets: Record<DashboardBucketKey, DashboardBucket>;
}

export async function getDashboardStats(): Promise<DashboardStatsResponse> {
  return apiFetch<DashboardStatsResponse>('/zos/dashboard-stats', {}, false);
}

export interface ZosUser {
  id: string;
  email: string | null;
  vorname: string | null;
  nachname: string | null;
  role: string | null;       // Multi-Role möglich: "office,techniker" — split bei Komma/Pipe/Whitespace
  telegram_chat_id?: string | null;
  display_name: string;
}

export interface ZosUsersResponse {
  ok: boolean;
  users: ZosUser[];
}

/** Liefert n8n-User. Achtung: `role` ist nicht für alle User gepflegt.
 *  Multi-Role-Support: User kann z.B. role="office,techniker" haben.
 *  Frontend filtert client-seitig via userHasRole(). */
export async function getZosUsers(role?: string): Promise<ZosUsersResponse> {
  const suffix = role ? `?role=${encodeURIComponent(role)}` : '';
  return apiFetch<ZosUsersResponse>(`/zos/users${suffix}`, {}, false);
}

/** Split role-String bei Komma/Pipe/Whitespace und liefert normalisierte Array.
 *  Beispiel: "office,techniker" → ["office","techniker"], null → []. */
export function userRoles(user: ZosUser): string[] {
  if (!user.role) return [];
  return user.role
    .toLowerCase()
    .split(/[,|\s]+/)
    .map((r) => r.trim())
    .filter(Boolean);
}

/** Prüft ob User mindestens eine der wanted-Rollen hat. STRIKT — null-role User zählt nicht.
 *  Beispiel: userHasRole(jonas, ['techniker']) → true wenn role="office,techniker". */
export function userHasRole(user: ZosUser, wanted: string[]): boolean {
  const got = userRoles(user);
  return wanted.some((w) => got.includes(w.toLowerCase()));
}

/** Convenience: ist der User ein Monteur/Techniker?
 *  Akzeptiert: techniker, tech, monteur. Pragmatik: wenn role gar nicht
 *  gepflegt ist (häufig, weil User-Tabelle noch nicht vollständig migriert),
 *  als möglicher Monteur durchlassen — sonst hätte das Frontend einen leeren
 *  Dropdown. Sobald role auf 'office'/'dispatcher'/'geschaeftsfuehrung' o.ä.
 *  gesetzt ist, wird sauber gefiltert. */
export function isTechnicianUser(user: ZosUser): boolean {
  // Whitelist hat Vorrang
  if (userHasRole(user, ['techniker', 'tech', 'monteur'])) return true;
  // Wenn role gar nicht gepflegt → Fallback "erlauben"
  if (!user.role || !user.role.trim()) return true;
  return false;
}

export async function getZosLeadDetail(leadId: string): Promise<ZosLeadDetailResponse> {
  const cacheKey = `lead-detail:${leadId}`;
  try {
    const r = await apiFetch<ZosLeadDetailResponse>(
      `/zos/lead-detail?id=${encodeURIComponent(leadId)}`,
      {},
      false,
    );
    void zosCachePut(cacheKey, r).catch(() => {/* Cache-Fehler nie fatal */});
    return r;
  } catch (err) {
    if (!isNetworkError(err)) throw err;
    const cached = await zosCacheGet(cacheKey).catch(() => undefined);
    if (!cached) throw err;
    return { ...(cached.data as ZosLeadDetailResponse), _offline: true, _cachedAt: cached.savedAt } as ZosLeadDetailResponse;
  }
}

export interface ZosLeadCreateInput {
  customer_name: string;
  customer_phone?: string;
  customer_email?: string;
  lead_type?: ZosLeadType;
  notes?: string;
  source?: string;                  // default 'phone' (geändert 26.05.)
  source_reference_id?: string;     // z.B. Ticket-Nr, Telefon-Nr, Vikunja-Task-Id
}

/** Lead-Quellen — Standard ist 'phone'. */
export const LEAD_SOURCE_OPTIONS: { value: string; label: string; refLabel: string; refPlaceholder: string }[] = [
  { value: 'phone',         label: 'Telefon',          refLabel: 'Telefon-Nr (oder Anruf-ID)',  refPlaceholder: '+49 …' },
  { value: 'email',         label: 'E-Mail',           refLabel: 'E-Mail-Adresse',              refPlaceholder: 'absender@…' },
  { value: 'zammad_ticket', label: 'Zammad-Ticket',    refLabel: 'Ticket-Nr',                   refPlaceholder: '#12345' },
  { value: 'vikunja_task',  label: 'Vikunja-Task',     refLabel: 'Task-Nr',                     refPlaceholder: 'TASK-…' },
  { value: 'web_form',      label: 'Web-Formular',     refLabel: 'Formular-ID',                 refPlaceholder: 'optional' },
  { value: 'referral',      label: 'Empfehlung',       refLabel: 'Wer hat empfohlen?',          refPlaceholder: 'Name / Firma' },
  { value: 'walkin',        label: 'Laufkundschaft',   refLabel: 'Notiz',                       refPlaceholder: 'optional' },
  { value: 'manual',        label: 'Manuell / sonstig', refLabel: 'Referenz',                   refPlaceholder: 'optional' },
];

/** Anzeige-Label für Source-Code (Fallback = Code selbst, capitalisiert). */
export function leadSourceLabel(source: string | null | undefined): string {
  if (!source) return '—';
  return LEAD_SOURCE_OPTIONS.find((o) => o.value === source)?.label || source;
}

export interface ZosLeadCreateResponse {
  ok: boolean;
  lead_id: string;
  lead_number: string;
}

export async function createZosLead(input: ZosLeadCreateInput): Promise<ZosLeadCreateResponse> {
  return apiFetch<ZosLeadCreateResponse>(
    '/zos/lead-create',
    { method: 'POST', body: JSON.stringify(input) },
    false,
  );
}

export interface ZosLeadPatchFields {
  customer_name?: string;
  customer_phone?: string;
  customer_email?: string;
  lead_type?: ZosLeadType;
  manufacturer?: string;
  heating_system?: string;
  property_type?: string;
  budget_range?: string;
  photos_complete?: boolean;
  heat_load_available?: boolean;
  energy_consultant?: boolean;
  appointment_required?: boolean;
  appointment_date?: string | null;
  offer_required?: boolean;
  offer_sent_at?: string | null;
  next_action?: string;
  next_action_due?: string | null;
  notes?: string;
  current_handler?: string;
  handler_role?: 'office' | 'tech' | 'management' | 'admin';
  assigned_technician_id?: string | null;  // V5: zugewiesener Monteur
  // V2 (Schema v9a)
  complexity_level?: ZosComplexityLevel;
  process_path?: ZosProcessPath;
  customer_status?: ZosCustomerStatus;
  qualification_result?: ZosQualificationResult;
  review_required?: boolean;
  management_override?: boolean;
}

export interface ZosLeadPatchResponse {
  lead: {
    id: string;
    lead_number: string;
    status: ZosLeadStatus;
    updated_at: string;
  };
  changed_fields: string[];
}

export async function patchZosLead(
  leadId: string,
  fields: ZosLeadPatchFields,
): Promise<ZosLeadPatchResponse> {
  // Härtung: UUID-Felder vor Send validieren — sonst crasht ZOS-6 beim
  // Postgres-Cast (z.B. "3" als assigned_technician_id) und Frontend
  // bekommt eine generische Fehlermeldung statt klarer Diagnose.
  const uuidRe = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const uuidFields: (keyof ZosLeadPatchFields)[] = ['assigned_technician_id', 'pds_customer_id'];
  for (const k of uuidFields) {
    const v = (fields as Record<string, unknown>)[k as string];
    if (v != null && v !== '' && typeof v === 'string' && !uuidRe.test(v)) {
      throw new Error(`Ungültige UUID für ${String(k)}: "${v}". Backend kann das nicht setzen — Backend-User-Liste (ZOS-26) prüft auf UUID-Format.`);
    }
  }
  const r = await apiFetch<ZosLeadPatchResponse>(
    '/zos/lead-patch',
    {
      method: 'POST',
      body: JSON.stringify({ lead_id: leadId, fields }),
    },
    false,
  );
  // Härtung: wenn Backend Felder still aus Whitelist filtert (Bug-Klasse wie
  // ZOS-18-Grundmodul-Filter), zeigt es Erfolg aber changed_fields ist leer.
  // Frontend würde "gespeichert" toasten obwohl in DB nichts ankam.
  const requested = Object.keys(fields).filter((k) => (fields as Record<string, unknown>)[k] !== undefined);
  const accepted = Array.isArray(r.changed_fields) ? r.changed_fields : [];
  // Nur Felder zählen die wirklich einen Wert tragen (null ist auch ein Wert: "zurücksetzen")
  const missing = requested.filter((k) => !accepted.includes(k));
  if (requested.length > 0 && missing.length === requested.length) {
    throw new Error(`Lead-Patch hat keine Felder geschrieben (${requested.join(', ')}). Backend-Allowlist prüfen.`);
  }
  if (missing.length > 0) {
    console.warn(`Lead-Patch: ${missing.length} von ${requested.length} Feldern wurden vom Backend ignoriert (${missing.join(', ')})`);
  }
  return r;
}

export interface ZosNoteResponse {
  ok: boolean;
  activity_id: string;
}

export async function addZosLeadNote(leadId: string, text: string): Promise<ZosNoteResponse> {
  return apiFetch<ZosNoteResponse>(
    '/zos/lead-note',
    {
      method: 'POST',
      body: JSON.stringify({ lead_id: leadId, text }),
    },
    false,
  );
}

export interface ZosStatusChangeResponse {
  ok: boolean;
  lead: {
    id: string;
    lead_number: string;
    status: ZosLeadStatus;
    sla_status: ZosSlaStatus;
    updated_at: string;
  };
  note_added: boolean;
  note_activity_id: string | null;
}

// ===== Contacts / Addresses =====

export type ContactLeadRole = 'kunde' | 'rechnung' | 'objektkontakt' | 'empfehler' | 'technischer_kontakt' | 'sonstig';
export type AddressLeadRole = 'objekt' | 'rechnung' | 'liefer' | 'sonstig';
export type PdsLinkStatus = 'local_only' | 'matched' | 'pending_create' | 'created' | 'sync_error';

export type PdsPersonForm = 'PRIVATPERSON' | 'FIRMA';
export type EkTyp = 'FESTNETZ' | 'MOBIL' | 'EMAIL' | 'WEB' | 'FAX';
export type EkKategorie = 'GESCHAEFTLICH' | 'PRIVAT' | 'SONSTIGE';
export type PostTyp = 'HAUPTANSCHRIFT' | 'RECHNUNGSADRESSE' | 'LIEFERADRESSE' | 'SONSTIGE' | 'OBJEKT';

export interface ZosCommunication {
  id?: string;
  typ: EkTyp;
  kategorie: EkKategorie;
  value: string;
  is_primary?: boolean;
  pds_uuid?: string | null;
  pds_sync_status?: PdsLinkStatus;
}

export interface ZosContactView {
  contact_id: string;
  lead_id: string;
  role: ContactLeadRole;
  is_primary: boolean;
  pds_uuid: string | null;
  pds_sync_status: PdsLinkStatus;
  pds_form: PdsPersonForm | null;
  name: string | null;             // PDS-konform: bei FIRMA = Firmenname, bei PERSON = Nachname
  vorname: string | null;
  titel: string | null;
  anrede: string | null;
  funktion: string | null;
  api_id: string | null;
  // Legacy (Kompatibilität — werden in v5 entfernt)
  firma?: string | null;
  nachname?: string | null;
  email?: string | null;
  telefon_primary?: string | null;
  telefon_mobil?: string | null;
  notes: string | null;
  communications: ZosCommunication[];
}

export interface ZosAddressView {
  address_id: string;
  lead_id: string;
  role: AddressLeadRole;
  is_primary: boolean;
  pds_uuid: string | null;
  pds_sync_status: PdsLinkStatus;
  typ: PostTyp;
  bezeichnung: string | null;
  strasse: string | null;
  hausnummer: string | null;
  plz: string | null;
  ort: string | null;
  land: string;
  zusatz: string | null;
  owner_contact_id: string | null;
  notes: string | null;
}

export interface ZosContactCreateInput {
  pds_form?: PdsPersonForm;
  name?: string;          // bei FIRMA = Firmenname, bei PERSON = Nachname
  vorname?: string;       // nur bei PRIVATPERSON
  titel?: string;
  anrede?: string;
  funktion?: string;
  api_id?: string;
  notes?: string;
  communications?: ZosCommunication[];
  // Legacy-Felder bleiben akzeptiert (Backend mappt um):
  firma?: string;
  nachname?: string;
  email?: string;
  telefon_primary?: string;
  telefon_mobil?: string;
}

export interface ZosAddressCreateInput {
  typ?: PostTyp;
  bezeichnung?: string;
  strasse?: string;
  hausnummer?: string;
  plz?: string;
  ort?: string;        // required server-side
  land?: string;
  zusatz?: string;
  owner_contact_id?: string;
  notes?: string;
}

export async function addContactCommunication(contactId: string, comm: Omit<ZosCommunication,'id'|'pds_uuid'|'pds_sync_status'>): Promise<{ ok: boolean; result: string }> {
  return apiFetch<{ ok: boolean; result: string }>(
    '/zos/contact-communication',
    { method: 'POST', body: JSON.stringify({ action: 'add', contact_id: contactId, ...comm }) },
    false);
}

export async function updateContactCommunication(commId: string, fields: Partial<ZosCommunication>): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean; result: string }>(
    '/zos/contact-communication',
    { method: 'POST', body: JSON.stringify({ action: 'update', comm_id: commId, fields }) },
    false);
}

export async function deleteContactCommunication(commId: string): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean; result: string }>(
    '/zos/contact-communication',
    { method: 'POST', body: JSON.stringify({ action: 'delete', comm_id: commId }) },
    false);
}

export interface ZosPdsSearchHit {
  pds_uuid: string;
  bezeichnung: string;
  vorname: string | null;
  nachname: string | null;
  form: 'PRIVATPERSON' | 'FIRMA' | string;
  strasse: string | null;
  plz: string | null;
  ort: string | null;
  kundennummer: string | null;
  email: string | null;
  telefon: string | null;
  api_id: string | null;
}

export async function createZosContact(input: ZosContactCreateInput): Promise<{ contact_id: string }> {
  const r = await apiFetch<{ ok: boolean; contact_id: string }>(
    '/zos/contact-create', { method: 'POST', body: JSON.stringify(input) }, false);
  return { contact_id: r.contact_id };
}

export async function updateZosContact(contactId: string, fields: Partial<ZosContactCreateInput>): Promise<void> {
  await apiFetch('/zos/contact-update',
    { method: 'POST', body: JSON.stringify({ contact_id: contactId, fields }) }, false);
}

export async function createZosAddress(input: ZosAddressCreateInput): Promise<{ address_id: string }> {
  const r = await apiFetch<{ ok: boolean; address_id: string }>(
    '/zos/address-create', { method: 'POST', body: JSON.stringify(input) }, false);
  return { address_id: r.address_id };
}

export async function updateZosAddress(addressId: string, fields: Partial<ZosAddressCreateInput>): Promise<void> {
  await apiFetch('/zos/address-update',
    { method: 'POST', body: JSON.stringify({ address_id: addressId, fields }) }, false);
}

export async function attachContactToLead(leadId: string, contactId: string, role: ContactLeadRole, isPrimary = false): Promise<{ link_id: string }> {
  const r = await apiFetch<{ ok: boolean; link_id: string }>(
    '/zos/lead-attach-contact',
    { method: 'POST', body: JSON.stringify({ lead_id: leadId, contact_id: contactId, role, is_primary: isPrimary }) },
    false);
  return { link_id: r.link_id };
}

/** Löst die Verknüpfung eines Kontakts zu einem Lead. Kontakt selbst bleibt erhalten. */
export async function detachContactFromLead(leadId: string, contactId: string): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>(
    '/zos/lead-detach-contact',
    { method: 'POST', body: JSON.stringify({ lead_id: leadId, contact_id: contactId }) },
    false);
}

export async function attachAddressToLead(leadId: string, addressId: string, role: AddressLeadRole, isPrimary = false): Promise<{ link_id: string }> {
  const r = await apiFetch<{ ok: boolean; link_id: string }>(
    '/zos/lead-attach-address',
    { method: 'POST', body: JSON.stringify({ lead_id: leadId, address_id: addressId, role, is_primary: isPrimary }) },
    false);
  return { link_id: r.link_id };
}

/** Lässt die KI den Fragebogen aus dem Ticket-Verlauf vorbefüllen.
 *
 *  Geschrieben wird nur, wo noch keine Antwort steht — Vorhandenes bleibt
 *  unangetastet. Die Vorschläge tragen `answered_by = 'ki:zos-61'`; solange
 *  welche offen sind, sperrt der Red Flag `KI_VORSCHLAEGE_OFFEN` Termin und
 *  Angebot. */
export async function leadVorbefuellen(leadId: string): Promise<{
  ok: boolean; vorschlaege: number; geschrieben?: string[];
  verworfen?: { key: string; grund: string }[]; hinweis?: string;
}> {
  return apiFetch('/zos/lead-vorbefuellen',
    { method: 'POST', body: JSON.stringify({ lead_id: leadId }) }, false);
}

/** Bestätigt KI-Vorschläge: setzt `answered_by` auf den angemeldeten Benutzer.
 *
 *  Ohne `questionKey` werden alle offenen Vorschläge des Leads bestätigt. Ein
 *  einzelnes Feld lässt sich auch dadurch bestätigen, dass man es anfasst und
 *  speichert — ZOS-18 überschreibt `answered_by` dabei ohnehin. */
export async function kiVorschlaegeBestaetigen(leadId: string, questionKey?: string): Promise<{
  ok: boolean; bestaetigt: number; fragen: string[];
}> {
  return apiFetch('/zos/ki-bestaetigen',
    { method: 'POST', body: JSON.stringify({ lead_id: leadId, question_key: questionKey }) }, false);
}

/** Löst eine Adresse vom Lead — über **alle** Rollen. Der Adressdatensatz
 *  bleibt in ZOS erhalten, genau wie bei `detachContactFromLead`. Eine
 *  PDS-Adresse kommt beim nächsten Sync wieder, weil sie in PDS existiert. */
export async function detachAddressFromLead(leadId: string, addressId: string): Promise<{ ok: boolean; roles_removed?: AddressLeadRole[] }> {
  return apiFetch<{ ok: boolean; roles_removed?: AddressLeadRole[] }>(
    '/zos/lead-detach-address',
    { method: 'POST', body: JSON.stringify({ lead_id: leadId, address_id: addressId }) },
    false);
}

/** Setzt die Rollen einer Adresse am Lead auf **genau** diese Liste.
 *
 *  Unterschied zu `attachAddressToLead`: Das hängt eine Rolle *zusätzlich* an.
 *  Wer damit eine Adresse nachträglich als Objekt kennzeichnete, hatte sie
 *  anschließend zweimal am Lead — einmal als Rechnung, einmal als Objekt. */
export async function setLeadAddressRoles(leadId: string, addressId: string, roles: AddressLeadRole[]): Promise<{ ok: boolean; added: number; removed: number }> {
  return apiFetch<{ ok: boolean; added: number; removed: number }>(
    '/zos/lead-address-roles',
    { method: 'POST', body: JSON.stringify({ lead_id: leadId, address_id: addressId, roles }) },
    false);
}

/** Hängt PDS-Adressen am Lead an und leitet die Rolle serverseitig aus
 *  `addresses.typ` ab (HAUPTANSCHRIFT → Objekt **und** Rechnung). Ersetzt die
 *  früheren Schleifen, die jede Adresse pauschal als 'rechnung' anhängten. */
export async function attachPdsAddressesToLead(leadId: string, addressIds: string[]): Promise<{ ok: boolean; attached: number; already_present: number }> {
  return apiFetch<{ ok: boolean; attached: number; already_present: number }>(
    '/zos/lead-attach-pds-addresses',
    { method: 'POST', body: JSON.stringify({ lead_id: leadId, address_ids: addressIds }) },
    false);
}

export async function searchPds(suchwort: string, entriesPerPage = 20): Promise<{ items: ZosPdsSearchHit[]; total: number }> {
  return apiFetch<{ ok: boolean; items: ZosPdsSearchHit[]; total: number }>(
    '/zos/pds-search',
    { method: 'POST', body: JSON.stringify({ suchwort, entriesPerPage }) },
    false);
}

/** Find-or-create: gibt contact_id zurück, der mit der PDS-UUID verlinkt ist.
 *  Wenn bereits ein Contact in ZOS mit dieser pds_uuid existiert → wird wiederverwendet (idempotent).
 *  Sonst → frischer Contact wird angelegt + verlinkt.
 */
export async function resolveContactByPds(hit: ZosPdsSearchHit): Promise<{ contact_id: string }> {
  const r = await apiFetch<{ ok: boolean; contact_id: string }>(
    '/zos/contact-by-pds',
    { method: 'POST', body: JSON.stringify({
        pds_uuid: hit.pds_uuid,
        form: hit.form,
        name: hit.bezeichnung || hit.nachname || hit.firma,
        vorname: hit.vorname,
        firma: hit.firma,
        nachname: hit.nachname,
        bezeichnung: hit.bezeichnung,
        telefon: hit.telefon,
        email: hit.email,
        kundennummer: hit.kundennummer,
    }) },
    false);
  return { contact_id: r.contact_id };
}

export async function linkContactToPds(contactId: string, pdsUuid: string, fields?: Partial<ZosPdsSearchHit>): Promise<void> {
  await apiFetch('/zos/contact-link-pds',
    { method: 'POST', body: JSON.stringify({ contact_id: contactId, pds_uuid: pdsUuid, fields: fields || {} }) },
    false);
}

export interface ZosPdsSyncResult {
  ok: boolean;
  contact_id: string;
  communications_synced: number;
  addresses_synced: number;
  address_ids: string[];
  hauptanschrift_id: string | null;
}

/** Holt POST /person/details aus PDS und schreibt EKommunikationswege + PostKommunikationswege
 *  idempotent in zos.contact_communications + zos.addresses. Gibt die address_ids zurück,
 *  damit der Aufrufer sie an den Lead hängen kann.
 */
export async function pdsSyncContactDetails(contactId: string, pdsUuid: string): Promise<ZosPdsSyncResult> {
  const r = await apiFetch<ZosPdsSyncResult>(
    '/zos/pds-sync-contact',
    { method: 'POST', body: JSON.stringify({ contact_id: contactId, pds_uuid: pdsUuid }) },
    false);
  return r;
}

// ===== Flow-Engine (Qualifizierung) =====

export type ZosAnswerType = 'single' | 'multi' | 'boolean' | 'free' | 'number' | 'date' | 'photo';

export type ZosQuestionKind = 'customer_question' | 'internal_assessment' | 'system_check' | 'script_hint';

export interface ZosFlowQuestion {
  id: string;
  position: number;
  question_key: string;
  question_text: string;
  answer_type: ZosAnswerType;
  options: { value: string; label: string }[];
  is_required: boolean;
  is_internal?: boolean;                       // Legacy seit v8a — neue Logik nutzt question_kind
  question_kind?: ZosQuestionKind;             // v8a
  section?: string | null;                     // v8a — z.B. "Kontakt", "Objekt", "Intern - Status"
  blocks_appointment?: boolean;                // v8a
  blocks_offer?: boolean;                      // v8a
  source_template_lead_type?: string | null;   // v8d — woher die Frage kommt (NULL = Grundmodul)
  parent_question_id: string | null;
  show_when: Record<string, unknown> | null;
  help_text: string | null;
  placeholder: string | null;
  contributes_to: string[];
  feeds_offer?: boolean;
  offer_field_key?: string | null;
}

export interface ZosFlowTemplate {
  id: string;
  lead_type: ZosLeadType;
  name: string;
  version: number;
  description: string | null;
}

export interface ZosFlowAnswer {
  question_id: string;
  question_key: string;
  answer_value: Record<string, unknown>;
  answered_by: string | null;
  answered_at: string;
}

export interface ZosFlowTemplateResponse {
  ok: boolean;
  template: ZosFlowTemplate | null;
  /** Seit v8d: Grundmodul-Template wird mitgeliefert. */
  template_grundmodul?: ZosFlowTemplate | null;
  questions: ZosFlowQuestion[];
  answers: ZosFlowAnswer[];
  /** Offline-Fallback (seit 09.06.): true wenn aus IndexedDB-Cache statt Netz. */
  _offline?: boolean;
  /** Zeitstempel des gecachten Stands (nur wenn _offline). */
  _cachedAt?: number;
}

export type ZosFlowType = 'QUALIFIKATION' | 'AUDIT' | 'NACHKAUF';

// =============================================================
// ZOS-28: PDS Person Lookup (Read-Only Wrapper um /person/details)
// =============================================================

export interface PdsCommunication {
  typ: 'FESTNETZ' | 'MOBIL' | 'EMAIL' | 'WEB' | 'FAX' | string;
  kategorie: 'GESCHAEFTLICH' | 'PRIVAT' | 'SONSTIGE' | string;
  value: string;
}

export interface PdsAddress {
  typ: string;     // HAUPTANSCHRIFT, RECHNUNG, LIEFER, OBJEKT, SONSTIG
  strasse: string | null;
  plz: string | null;
  ort: string | null;
  land: string | null;
}

/** Kennzeichen aus den PDS-Infofeldern — für den Bearbeiter am Telefon das
 *  Wichtigste an einem Bestandskunden. Null, wenn in PDS nicht gepflegt. */
export interface PdsKennzeichen {
  kundenstatus: string | null;
  notdienst: string | null;
  wartungsvertrag: string | null;
}

export interface PdsPersonLookup {
  ok: boolean;
  uuid?: string;
  name?: string | null;
  vorname?: string | null;
  anrede?: string | null;
  form?: 'PRIVATPERSON' | 'FIRMA' | null;
  kundennummer?: string | null;
  phones?: PdsCommunication[];
  emails?: PdsCommunication[];
  hauptanschrift?: PdsAddress | null;
  addresses?: PdsAddress[];
  kennzeichen?: PdsKennzeichen | null;
  error?: string;
}

/** Holt PDS-Personen-Details VOR Lead-Anlage (Read-Only, kein DB-Side-Effect).
 *  Für UI-Prefill nach Klick auf PDS-Suchergebnis. */
export async function lookupPdsPerson(pdsUuid: string): Promise<PdsPersonLookup> {
  return apiFetch<PdsPersonLookup>(
    `/zos/pds-person-lookup?uuid=${encodeURIComponent(pdsUuid)}`,
    {}, false,
  );
}

/** Ein Objekt des Kunden aus PDS, mit seiner Standortadresse. */
export interface PdsObjekt {
  uuid: string;
  bezeichnung: string | null;
  typ: string | null;
  strasse: string | null;
  plz: string | null;
  ort: string | null;
  zusatz: string | null;
  adresse_uuid: string | null;
  hat_abweichende_rechnungsadresse: boolean;
}

/** ZOS-52 — die Objekte eines PDS-Kunden samt Standortadresse.
 *
 *  PDS kann `objekt/listobjekte` nur per Freitext durchsuchen, nicht nach Kunde
 *  filtern. Deshalb braucht der Aufruf den Kundennamen als Suchwort; die
 *  Zuordnung passiert serverseitig über die `personUUID` an jedem Objekt.
 */
export async function listPdsObjekte(
  personUuid: string, suchwort: string,
): Promise<{ ok: boolean; objekte: PdsObjekt[]; eigene?: number; error?: string }> {
  const r = await apiFetch<{ ok: boolean; objekte?: PdsObjekt[]; eigene?: number; error?: string }>(
    `/zos/pds-objekte?person_uuid=${encodeURIComponent(personUuid)}&suchwort=${encodeURIComponent(suchwort)}`,
    {}, false,
  );
  return { ok: !!r.ok, objekte: r.objekte ?? [], eigene: r.eigene, error: r.error };
}

/** Mapping: Audit-Frage → Qualifizierungs-Quell-Frage.
 *  Wenn Lead in der Qualifizierung schon eine Antwort hat, wird sie im Audit-Wizard
 *  vorbefüllt (mit visuellem Hinweis „aus Qualifizierung übernommen — anpassen möglich").
 *  Nur 1:1-kompatible Mappings hier — komplexere Wert-Transformationen via `transform()`.
 */
export interface AuditPrefillRule {
  fromKey: string;
  /** Optional: Wert-Transformation (z.B. Werte-Mapping zwischen Optionen) */
  transform?: (value: unknown) => unknown;
}

export const AUDIT_PREFILL_MAP: Record<string, AuditPrefillRule> = {
  // 1:1 number (identische Semantik)
  'audit_wohnflaeche_total':              { fromKey: 'wohnflaeche' },
  'audit_existing_heating_install_year':  { fromKey: 'bestandsanlage_baujahr' },

  // single → single mit Wert-Konvertierung wo nötig
  'audit_existing_heating_type': {
    fromKey: 'current_heating_type',
    transform: (v) => {
      const m: Record<string, string> = {
        gas: 'gas', oel: 'oel', waermepumpe: 'waermepumpe',
        pellet_holz: 'holz', fernwaerme: 'fernwaerme',
        nachtspeicher: 'nachtspeicher', elektro_direkt: 'sonstig',
        keine_heizung: 'keine', unbekannt: 'sonstig',
      };
      return m[String(v)] || v;
    },
  },
  'audit_heat_distribution_summary': {
    fromKey: 'heizflaechen',
    transform: (v) => {
      const m: Record<string, string> = {
        heizkoerper: 'heizkoerper', fussbodenheizung: 'fbh',
        gemischt: 'mix', unbekannt: 'unklar',
      };
      return m[String(v)] || v;
    },
  },
  'audit_preferred_manufacturer_confirmed': {
    fromKey: 'preferred_manufacturer',
    // Werte identisch: bosch / zewotherm / offen (in V3 hart auf diese 3 reduziert)
  },
  'audit_funding_known': {
    fromKey: 'foerderung_interesse',
    transform: (v) => v === 'energieberater_vorhanden' || v === 'interesse_foerderung',
  },
  'audit_desired_heat_source': {
    fromKey: 'desired_solution',
    transform: (v) => {
      // desired_solution: waermepumpe/hybrid/offen/andere → audit_desired_heat_source: luft_wasser/sole_wasser_*/egal
      // Mapping ist verlustig — nur offen → egal sicher
      if (v === 'offen') return 'egal';
      // sonst nicht prefillen
      return undefined;
    },
  },

  // ===== HEATING_CONVENTIONAL-spezifisch (v8) =====
  // Wunschsystem aus Qualifizierungs-desired_solution
  'audit_desired_system_type': {
    fromKey: 'desired_solution',
    transform: (v) => {
      // Qualifizierung: waermepumpe/hybrid/offen/andere_vorstellung
      // HEATING-Audit-Optionen: brennwert_gas/brennwert_oel/hybrid_gas_solar/hybrid_oel_solar/pellet/fernwaerme_anschluss/offen
      if (v === 'hybrid') return 'hybrid_gas_solar'; // beste Annahme
      if (v === 'offen') return 'offen';
      return undefined;
    },
  },

  // ===== BATHROOM-spezifisch (v8) =====
  'audit_bath_subtype_confirmed': {
    fromKey: 'bath_subtype',
    // Werte sollten identisch sein: haupt_bad/gaeste_wc/beides/mietwohnung_bad/teilarbeiten
  },
  'audit_accessibility_needs': {
    fromKey: 'accessibility_needs',
    // Werte identisch wenn die Qualifizierungs-Frage angelegt wird
  },
};

/** Berechnet die Prefill-Map für ein Audit aus den Qualifizierungs-Antworten.
 *  Output: { audit_question_key: extracted_value } — nur für Keys mit Match. */
export function buildAuditPrefill(qualiAnswers: ZosFlowAnswer[]): Record<string, unknown> {
  const byKey: Record<string, unknown> = {};
  for (const a of qualiAnswers) {
    const v = a.answer_value || {};
    // Wert extrahieren — kompatibel mit allen answer_types
    const raw = (v as Record<string, unknown>).option
      ?? (v as Record<string, unknown>).text
      ?? (v as Record<string, unknown>).bool
      ?? (v as Record<string, unknown>).number
      ?? (v as Record<string, unknown>).options;
    if (raw !== undefined && raw !== null) byKey[a.question_key] = raw;
  }
  const out: Record<string, unknown> = {};
  for (const [auditKey, rule] of Object.entries(AUDIT_PREFILL_MAP)) {
    const src = byKey[rule.fromKey];
    if (src === undefined || src === null) continue;
    const transformed = rule.transform ? rule.transform(src) : src;
    if (transformed === undefined || transformed === null) continue;
    out[auditKey] = transformed;
  }
  return out;
}

export async function getZosFlowTemplate(
  leadId: string,
  flowType: ZosFlowType = 'QUALIFIKATION'
): Promise<ZosFlowTemplateResponse> {
  const cacheKey = `flow-template:${leadId}:${flowType}`;
  try {
    const r = await apiFetch<ZosFlowTemplateResponse>(
      `/zos/flow-template?lead_id=${encodeURIComponent(leadId)}&flow_type=${flowType}`,
      {}, false);
    // Erfolgreiche Antwort cachen — damit der Wizard offline öffnen kann
    void zosCachePut(cacheKey, r).catch(() => {/* Cache-Fehler nie fatal */});
    return r;
  } catch (err) {
    if (!isNetworkError(err)) throw err;
    // Offline-Fallback: letzter gecachter Stand + noch nicht gesyncte Outbox-Antworten drüberlegen
    const cached = await zosCacheGet(cacheKey).catch(() => undefined);
    if (!cached) throw err;
    const data = cached.data as ZosFlowTemplateResponse;
    const pending = await zosOutboxByLead(leadId).catch(() => []);
    const mergedAnswers = [...(data.answers || [])];
    for (const p of pending) {
      const idx = mergedAnswers.findIndex((a) => a.question_key === p.questionKey);
      const overlay = { question_key: p.questionKey, answer_value: p.value } as ZosFlowAnswer;
      if (idx >= 0) mergedAnswers[idx] = { ...mergedAnswers[idx], ...overlay };
      else mergedAnswers.push(overlay);
    }
    return { ...data, answers: mergedAnswers, _offline: true, _cachedAt: cached.savedAt };
  }
}

// =============================================================
// ZOS-30 / ZOS-31 — Scoring-Regeln (Settings-Seite, GF/Admin)
// =============================================================

export interface ZosScoringFactor {
  id: string;
  factor_key: string;
  label: string;
  category: string;
  lead_type_filter: string | null;
  score_impact: number;
  is_killer: boolean;
  condition: Record<string, unknown> | null;
  is_active: boolean;
  description: string | null;
  /** seit v15 — Dimension (BUYING_INTENT, ECONOMICS, etc.) */
  dimension?: string | null;
  /** seit v16 — Map {option_value: score_impact} fuer Multi-Choice-Fragen. */
  option_score_map?: Record<string, number> | null;
  /** seit v16 — question_key der referenzierten Frage wenn Map verwendet wird. */
  map_question_key?: string | null;
}

/** Priority-Schwellen (seit v16, editierbar via Settings). */
export interface ZosPriorityThreshold {
  priority: 'A' | 'B' | 'C' | 'D';
  min_score: number;
  min_completion: number;
  description: string | null;
}
export interface ZosPriorityThresholdsResponse {
  ok: boolean;
  thresholds: ZosPriorityThreshold[];
}

export async function getZosPriorityThresholds(): Promise<ZosPriorityThresholdsResponse> {
  return apiFetch('/zos/priority-thresholds', {}, false);
}

export async function writeZosPriorityThreshold(
  priority: 'A' | 'B' | 'C' | 'D', minScore: number, minCompletion: number,
): Promise<{ ok: boolean; row: ZosPriorityThreshold }> {
  return apiFetch('/zos/priority-thresholds-write', {
    method: 'POST',
    body: JSON.stringify({ priority, min_score: minScore, min_completion: minCompletion }),
  }, false);
}

export interface ZosRedFlagRule {
  id: string;
  flag_code: string;
  label: string;
  severity: 'info' | 'warning' | 'critical' | string;
  /** seit v11 — NULL = global (alle Lead-Types). */
  lead_type_filter: string | null;
  condition: Record<string, unknown> | null;
  blocks_appointment: boolean;
  blocks_offer: boolean;
  requires_review: boolean;
  triggers_consulting_mode: boolean;
  is_active: boolean;
  description: string | null;
}

export interface ZosCompletionRequirement {
  id: string;
  requirement_key: string;
  label: string;
  classification: 'required' | 'recommended' | 'optional' | string;
  lead_type: string;
  check_definition: Record<string, unknown> | null;
  affects: string[] | null;
  is_active: boolean;
  description: string | null;
}

export interface ZosScoringRulesResponse {
  ok: boolean;
  scoring_factors: ZosScoringFactor[];
  red_flag_rules: ZosRedFlagRule[];
  completion_requirements: ZosCompletionRequirement[];
}

export type ZosRuleTable = 'scoring_factors' | 'red_flag_rules' | 'completion_requirements';

export async function getZosScoringRules(): Promise<ZosScoringRulesResponse> {
  return apiFetch<ZosScoringRulesResponse>('/zos/scoring-rules', {}, false);
}

export async function toggleZosScoringRule(table: ZosRuleTable, id: string, isActive: boolean): Promise<{ ok: boolean; id: string; is_active: boolean; label?: string }> {
  return apiFetch('/zos/scoring-rule-toggle', {
    method: 'POST',
    body: JSON.stringify({ table, id, is_active: isActive }),
  }, false);
}

// =============================================================
// Question-Editor (Phase C, seit 2026-06-01) — flow_questions CRUD
// Backend: zos.question_write + zos.question_reorder + zos.questions_by_template
//          n8n ZOS-36 (write) + ZOS-37 (read by template)
// =============================================================

/** Erweiterte Frage-Felder fuer den Editor (mehr als ZosFlowQuestion, das nur fuer Wizard ist). */
export interface ZosFlowQuestionFull extends ZosFlowQuestion {
  template_id: string;
  is_active: boolean;
  mirrors_question_key?: string | null;
  mirror_transform?: Record<string, unknown> | null;
  created_at?: string;
  updated_at?: string;
}

export interface ZosQuestionsByTemplateResponse {
  ok: boolean;
  template_id: string;
  questions: ZosFlowQuestionFull[];
}

/** Whitelist UI-editierbarer Felder (mirror server-side in zos.question_write). */
export const QUESTION_EDITABLE_FIELDS = [
  'question_text','help_text','placeholder','options','show_when','position','section',
  'is_required','blocks_appointment','blocks_offer','feeds_offer','offer_field_key',
  'contributes_to','is_internal','question_kind','mirrors_question_key','mirror_transform',
  'is_active',
] as const;

/** Pflichtfelder bei CREATE. */
export const QUESTION_CREATE_REQUIRED = [
  'template_id','question_key','question_text','answer_type','position',
] as const;

export type ZosQuestionAction = 'update' | 'create' | 'delete' | 'reorder';

// =============================================================
// Template-Editor + Effective-View (Phase I, seit 2026-06-04)
// =============================================================

export interface ZosFlowTemplateFull {
  id: string;
  lead_type: string | null;
  flow_type: ZosFlowType;
  name: string;
  description: string | null;
  version: number;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
  questions_active: number;
  questions_total: number;
}

export interface ZosTemplatesAllResponse {
  ok: boolean;
  templates: ZosFlowTemplateFull[];
}

export async function getZosFlowTemplatesAll(): Promise<ZosTemplatesAllResponse> {
  return apiFetch('/zos/flow-templates-all', {}, false);
}

export type ZosTemplateAction = 'update' | 'create' | 'delete';

export async function writeZosFlowTemplate(
  action: ZosTemplateAction,
  opts: { id?: string | null; payload?: Record<string, unknown> },
): Promise<{ ok: boolean; action: ZosTemplateAction; row: Record<string, unknown> }> {
  return apiFetch('/zos/template-write', {
    method: 'POST',
    body: JSON.stringify({ action, id: opts.id, payload: opts.payload }),
  }, false);
}

/** Frage in Effective-View (Phase J: Single-Template, kein Versions-Konzept user-facing). */
export interface ZosEffectiveQuestion extends ZosFlowQuestionFull {
  template_lead_type: string | null;
  template_flow_type: ZosFlowType;
  source_scope: 'GRUNDMODUL' | 'LEAD_TYPE';  // ist Frage Grundmodul-weit oder lead-type-spezifisch?
  origin: 'STANDARD' | 'EIGENE';              // vom System geliefert oder vom User erstellt?
  is_default_question: boolean;
  /** v23: Wie viele aktive Scoring-Regeln / Red-Flags / Pflichtangaben referenzieren diese Frage?
   *  Editor warnt beim Deaktivieren wenn > 0. */
  used_by_rules?: number;
  used_by_flags?: number;
  used_by_requirements?: number;
}

export interface ZosQuestionsEffectiveResponse {
  ok: boolean;
  lead_type: string | null;
  flow_type: ZosFlowType;
  grundmodul_template_id: string | null;
  lead_type_template_id: string | null;
  questions: ZosEffectiveQuestion[];
}

/** question_key → Frage-Text Mapping (ZOS-50). Für den Scoring-Editor:
 *  "zu welcher Frage gehört diese Regel?" */
export async function getZosQuestionLabels(): Promise<{ ok: boolean; labels: Record<string, string> }> {
  return apiFetch('/zos/question-labels', {}, false);
}

export async function getZosQuestionsEffective(
  leadType: string | null = null,
  flowType: ZosFlowType = 'QUALIFIKATION',
  includeInactive: boolean = true,
): Promise<ZosQuestionsEffectiveResponse> {
  const params = new URLSearchParams();
  if (leadType) params.set('lead_type', leadType);
  params.set('flow_type', flowType);
  params.set('include_inactive', String(includeInactive));
  return apiFetch(`/zos/questions-effective?${params.toString()}`, {}, false);
}

export async function getZosQuestionsByTemplate(templateId: string): Promise<ZosQuestionsByTemplateResponse> {
  return apiFetch<ZosQuestionsByTemplateResponse>(`/zos/questions-by-template?template_id=${encodeURIComponent(templateId)}`, {}, false);
}

export async function writeZosQuestion(
  action: ZosQuestionAction,
  opts: { id?: string | null; payload?: Record<string, unknown>; templateId?: string; orderedIds?: string[] },
): Promise<{ ok: boolean; action: ZosQuestionAction; row?: Record<string, unknown>; reordered?: number }> {
  const body: Record<string, unknown> = { action };
  if (opts.id) body.id = opts.id;
  if (opts.payload) body.payload = opts.payload;
  if (action === 'reorder') {
    body.template_id = opts.templateId;
    body.ordered_ids = opts.orderedIds;
  }
  return apiFetch('/zos/question-write', { method: 'POST', body: JSON.stringify(body) }, false);
}

// =============================================================
// User-Permissions (Phase F, seit 2026-06-03)
// =============================================================

export interface ZosUserPermissions {
  can_delete_leads: boolean;
  can_restore_leads: boolean;
}

// =============================================================
// System-Settings (Phase H, seit 2026-06-03)
// =============================================================

export type ZosSystemSettingKey = 'lead_dedup_mode' | 'lead_dedup_window_days';
export interface ZosSystemSetting {
  key: ZosSystemSettingKey | string;
  value: unknown;
  description?: string | null;
  updated_at?: string;
  updated_by?: string | null;
}
export type ZosDedupMode = 'detect' | 'warn' | 'block';

export async function getZosSystemSettings(): Promise<{ ok: boolean; settings: ZosSystemSetting[] }> {
  return apiFetch('/zos/system-settings', {}, false);
}
export async function writeZosSystemSetting(key: string, value: unknown): Promise<{ ok: boolean }> {
  return apiFetch('/zos/system-settings-write', {
    method: 'POST',
    body: JSON.stringify({ key, value }),
  }, false);
}

export async function getZosUserPermissions(): Promise<{ ok: boolean; email: string; permissions: ZosUserPermissions }> {
  return apiFetch('/zos/user-permissions', {}, false);
}

/** Lead archivieren (Soft-Delete) mit Pflicht-Begruendung.
 *  Server-side Permission-Check (can_delete_leads). Server-Validierung: reason min 10 Zeichen. */
export async function archiveZosLead(leadId: string, reason: string): Promise<{ ok: boolean; lead_id: string; archived_at: string }> {
  return apiFetch('/zos/lead-archive', {
    method: 'POST',
    body: JSON.stringify({ lead_id: leadId, reason }),
  }, false);
}

/** Lead wiederherstellen. Permission: can_restore_leads. */
export async function restoreZosLead(leadId: string): Promise<{ ok: boolean; lead_id: string; restored_at: string }> {
  return apiFetch('/zos/lead-restore', {
    method: 'POST',
    body: JSON.stringify({ lead_id: leadId }),
  }, false);
}

/** Bulk-Recompute aller aktiven Leads (status NOT IN won/lost/archived).
 *  Aufruf nach Settings-/Frage-Aenderungen damit completion_breakdown +
 *  scoring + red_flags konsistent neu berechnet werden.
 *  Performance: ~5ms pro Lead. Bei >100 Leads spaeter async via Cron. */
export async function recomputeAllZosLeads(): Promise<{ ok: boolean; recomputed: number; duration_ms: number; tenant_id: string }> {
  return apiFetch('/zos/recompute-all', { method: 'POST', body: '{}' }, false);
}

/** Editierbare Felder pro Regel-Tabelle (mirrors zos.rule_write Whitelist).
 *  WICHTIG: condition/check_definition/factor_key/flag_code/requirement_key sind bewusst NICHT
 *  editierbar via UI — Migration-only. */
export const RULE_EDITABLE_FIELDS: Record<ZosRuleTable, string[]> = {
  scoring_factors: ['label','description','category','lead_type_filter','score_impact','is_killer','is_active'],
  red_flag_rules: ['label','description','severity','lead_type_filter','blocks_appointment','blocks_offer','requires_review','triggers_consulting_mode','is_active'],
  completion_requirements: ['label','description','classification','lead_type','affects','is_active'],
};

/** Pflichtfelder fuer CREATE (mirrors zos.rule_write). */
export const RULE_CREATE_REQUIRED: Record<ZosRuleTable, string[]> = {
  scoring_factors: ['factor_key','label','category','score_impact','condition'],
  red_flag_rules: ['flag_code','label','severity','condition'],
  completion_requirements: ['requirement_key','label','classification','lead_type','check_definition'],
};

export type ZosRuleAction = 'update' | 'create' | 'delete';

export interface ZosRuleWriteResponse {
  ok: boolean;
  action: ZosRuleAction;
  row: Record<string, unknown>;
}

/** UPDATE/CREATE/DELETE auf einer Regel-Tabelle.
 *  - update: id Pflicht, payload = nur die zu aendernden Felder (Whitelist server-side enforced)
 *  - create: id NULL, payload = volle Regel inkl. factor_key/condition (Migration-Equivalent)
 *  - delete: id Pflicht, payload ignoriert */
export async function writeZosScoringRule(
  action: ZosRuleAction,
  table: ZosRuleTable,
  id: string | null,
  payload: Record<string, unknown> = {},
): Promise<ZosRuleWriteResponse> {
  return apiFetch<ZosRuleWriteResponse>('/zos/scoring-rule-write', {
    method: 'POST',
    body: JSON.stringify({ action, table, id, payload }),
  }, false);
}

export async function setZosFlowAnswer(leadId: string, questionKey: string, answerValue: Record<string, unknown>): Promise<{ ok: boolean; answer_id: string; question_key: string; queued?: boolean }> {
  try {
    const r = await apiFetch<{ ok: boolean; answer_id?: string; question_key?: string; error?: string }>(
      '/zos/flow-answer',
      { method: 'POST', body: JSON.stringify({ lead_id: leadId, question_key: questionKey, answer_value: answerValue }) },
      false);
    // Härtung: wenn Backend ok:false sendet (z.B. QUESTION_NOT_IN_TEMPLATE), als
    // echten Fehler weitergeben — sonst zeigt das Frontend fälschlich "gespeichert"
    // obwohl in DB nichts ankam.
    if (!r.ok || !r.answer_id) {
      throw new Error(r.error ? `Antwort nicht gespeichert (${r.error})` : 'Antwort konnte nicht gespeichert werden');
    }
    return { ok: true, answer_id: r.answer_id, question_key: r.question_key ?? questionKey };
  } catch (err) {
    // Offline / Netzwerkfehler → Antwort in die Outbox (last-value-wins pro Frage),
    // Sync läuft automatisch beim nächsten Online-Gehen (syncZosAnswerOutbox).
    // Backend-Fehler (Validation etc.) laufen NICHT hierher — die sollen sichtbar scheitern.
    if (isNetworkError(err)) {
      await zosOutboxPut(leadId, questionKey, answerValue);
      return { ok: true, answer_id: 'offline-pending', question_key: questionKey, queued: true };
    }
    throw err;
  }
}

/** Outbox-Sync: schickt offline eingegebene Wizard-Antworten ans Backend.
 *  Wird beim Online-Gehen + App-Start aufgerufen (initOfflineSync in offlineQueue.ts).
 *  Nutzt den AKTUELLEN Token (nicht den vom Eingabe-Zeitpunkt) — Token-Ablauf
 *  über Nacht killt die Antworten damit nicht. */
export async function syncZosAnswerOutbox(): Promise<{ synced: number; failed: number }> {
  const entries = await zosOutboxAll().catch(() => []);
  if (entries.length === 0) return { synced: 0, failed: 0 };

  let synced = 0, failed = 0;
  for (const e of entries) {
    try {
      const r = await apiFetch<{ ok: boolean; answer_id?: string; error?: string }>(
        '/zos/flow-answer',
        { method: 'POST', body: JSON.stringify({ lead_id: e.leadId, question_key: e.questionKey, answer_value: e.value }) },
        false);
      if (r.ok && r.answer_id) {
        await zosOutboxRemove(e.id);
        synced++;
      } else {
        // Backend lehnt ab (z.B. Frage gelöscht) — Eintrag verwerfen, User informieren
        await zosOutboxRemove(e.id);
        failed++;
        toast.warning(`Offline-Antwort zu „${e.questionKey}" wurde vom Server abgelehnt${r.error ? ` (${r.error})` : ''}.`);
      }
    } catch (err) {
      if (isNetworkError(err)) {
        // Immer noch offline — Rest bleibt liegen, nächster Versuch beim nächsten Online-Event
        break;
      }
      await zosOutboxRemove(e.id);
      failed++;
      toast.warning(`Offline-Antwort zu „${e.questionKey}" konnte nicht übertragen werden.`);
    }
  }
  if (synced > 0) {
    toast.success(`${synced} offline gespeicherte Antwort${synced === 1 ? '' : 'en'} übertragen`);
  }
  return { synced, failed };
}

/** Anzahl wartender Offline-Antworten (für UI-Badge). */
export async function getZosOutboxCount(): Promise<number> {
  return zosOutboxCount().catch(() => 0);
}

// ===== Lead-Detail-Response erweitert =====

export async function changeZosLeadStatus(
  leadId: string,
  toStatus: ZosLeadStatus,
  note?: string,
): Promise<ZosStatusChangeResponse> {
  return apiFetch<ZosStatusChangeResponse>(
    '/zos/lead-status',
    {
      method: 'POST',
      body: JSON.stringify({ lead_id: leadId, to_status: toStatus, note: note || '' }),
    },
    false,
  );
}

// Delete Leckortung Draft - POST /leckortung/delete-draft
export async function deleteLeckortungReport(id: string): Promise<{ success: boolean }> {
  const token = getToken();
  if (!token) throw new Error("Nicht angemeldet");

  if (isTokenExpired(token)) {
    removeToken();
    redirectToLogin();
    throw new Error("Token abgelaufen");
  }

  const response = await fetch(`${API_BASE}/leckortung/delete-draft`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ id }),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Fehler beim Löschen des Berichts");
  }

  return { success: true };
}

// ============================================================
// ZOS Artifacts — Lead-Anhänge (Bilder, Dokumente, Sprachnotizen)
// ============================================================

export interface ZosArtifactUploadInput {
  lead_id: string;
  kind: ZosArtifactKind;
  filename: string;
  mime: string;
  description?: string;
  context?: string;
  taken_at?: string;
  /** RAW base64 ohne data-URL-Prefix */
  data_base64: string;
}

export interface ZosArtifactUploadResult {
  ok: boolean;
  artifact_id: string;
  storage_path: string;
  kind: ZosArtifactKind;
  bytes_size: number;
  mime: string | null;
  description: string | null;
  context: string | null;
  taken_at: string | null;
  uploaded_by: string | null;
  created_at: string | null;
}

export async function uploadZosArtifact(input: ZosArtifactUploadInput): Promise<ZosArtifactUploadResult> {
  return apiFetch<ZosArtifactUploadResult>(
    '/zos/artifact-upload',
    { method: 'POST', body: JSON.stringify(input) },
    false
  );
}

/** Liest einen Anhang als data-URL ein (z.B. für <img src=...>). */
export async function fetchZosArtifactDataUrl(artifactId: string): Promise<{ mime: string; kind: ZosArtifactKind; dataUrl: string }> {
  const r = await apiFetch<{ ok: boolean; data_base64: string; mime: string; kind: ZosArtifactKind }>(
    `/zos/artifact?id=${encodeURIComponent(artifactId)}`, {}, false
  );
  return { mime: r.mime, kind: r.kind, dataUrl: `data:${r.mime};base64,${r.data_base64}` };
}

export async function deleteZosArtifact(artifactId: string): Promise<{ ok: boolean; deleted: string }> {
  return apiFetch<{ ok: boolean; deleted: string }>(
    '/zos/artifact-delete',
    { method: 'POST', body: JSON.stringify({ id: artifactId }) },
    false
  );
}

/** Heuristik: mime → kind ableiten, damit der Aufrufer das nicht hardcoden muss. */
export function deriveArtifactKind(mime: string): ZosArtifactKind {
  if (mime.startsWith('image/')) return 'photo';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  if (mime === 'application/pdf' || mime.startsWith('text/')) return 'document';
  return 'document';
}

// ─────────────────────────────────────────────────────────────────────
// Projektcontrolling — Baustellen-Tagesbericht
// Vertrag: crm/docs/API-PROJEKTCONTROLLING.md (PC-4/PC-5/PC-9/PC-13)
//
// Ersetzt die Erstentwurf-Funktionen getProjects/searchProjects/
// saveTagesberichtReport, deren Endpunkte in n8n nie existiert haben.
// ─────────────────────────────────────────────────────────────────────

/**
 * PC-9 — Projektakten aus PDS suchen. Das Suchwort geht durch bis
 * `projektakte/listprojektakten`; es wird nichts zwischengespeichert, damit eine
 * frisch angelegte Akte sofort auftaucht.
 */
export async function pcGetMyProjects(search?: string): Promise<PcProject[]> {
  const q = search && search.trim() ? `?search=${encodeURIComponent(search.trim())}` : "";
  const r = await apiFetch<{ ok: boolean; projekte: PcProject[] }>(`/pc/my-projects${q}`, {}, false);
  return r.projekte ?? [];
}

/** PC-13 — die Mannschaft für die Stundenzeilen (aktive Mitarbeiter). */
export async function pcGetEmployees(): Promise<PcEmployee[]> {
  const r = await apiFetch<{ ok: boolean; employees: PcEmployee[] }>("/pc/employees", {}, false);
  return r.employees ?? [];
}

/**
 * PC-5 — Aufnahme hochladen, Transkript + Vorbefüllung zurückbekommen.
 *
 * Wie bei `transcribeAudio` gilt: multipart/form-data, und offline geht das nicht
 * in die Outbox — der Monteur füllt dann von Hand aus. Deshalb eine klare Meldung
 * statt eines nichtssagenden Fehlers.
 */
export async function pcExtractReport(audioBlob: Blob, projekt: PcProject): Promise<PcExtractResponse> {
  assertVoiceOnline();

  const formData = new FormData();
  formData.append("audio", audioBlob, "bericht.webm");
  formData.append("projektakte_uuid", projekt.uuid);
  formData.append("projekt_name", projekt.name);

  const token = getToken();
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const response = await fetch(`${API_BASE}/pc/reports/extract`, {
    method: "POST",
    headers,
    body: formData,
  });

  if (!response.ok) {
    throw new Error("Auswertung der Aufnahme fehlgeschlagen");
  }
  return response.json();
}

/**
 * PC-4 — Bericht speichern. Läuft absichtlich über die normale Outbox:
 * Ein Bericht ohne Netz darf nicht verloren gehen, das PDF wird serverseitig
 * nachgezogen. Ein fehlgeschlagener PDF-Upload verwirft den Bericht nicht.
 */
export async function pcSaveReport(payload: PcReportPayload): Promise<PcReportSaveResponse> {
  return apiFetch<PcReportSaveResponse>("/pc/reports", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/**
 * PC-23 — das PC-4-PDF eines Berichts holen. Kein apiFetch: die Antwort ist
 * binär, nicht JSON. Wirft mit Klartext, wenn kein PDF hinterlegt ist oder
 * PDS nicht erreichbar war — der Aufrufer zeigt das als Toast.
 */
export async function pcHoleBerichtPdf(reportId: string): Promise<Blob> {
  const token = getToken();
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const response = await fetch(`${API_BASE}/pc/report-pdf?id=${encodeURIComponent(reportId)}`, { headers });

  if (response.status === 401) {
    removeToken();
    redirectToLogin();
    throw new Error("Nicht autorisiert");
  }
  if (response.status === 404) {
    throw new Error("Für diesen Bericht liegt kein PDF vor");
  }
  if (response.status === 403) {
    throw new Error("Kein Zugriff auf diesen Bericht");
  }
  if (!response.ok) {
    throw new Error("PDF konnte nicht geladen werden");
  }
  return response.blob();
}

/**
 * Holt das PDF über {@link pcHoleBerichtPdf} und öffnet es in einem neuen Tab.
 * Das leere Fenster muss synchron im Klick-Handler entstehen, VOR dem await —
 * danach sieht der Browser keine Nutzergeste mehr und blockt window.open()
 * lautlos als Popup, ohne Fehler und ohne Tab. Wirft weiter, der Aufrufer
 * entscheidet über Ladezustand/Toast.
 */
export async function pcBerichtPdfOeffnen(reportId: string): Promise<void> {
  const fenster = window.open('', '_blank');
  try {
    const blob = await pcHoleBerichtPdf(reportId);
    const url = URL.createObjectURL(blob);
    if (fenster) fenster.location.href = url;
    else window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e) {
    fenster?.close();
    throw e;
  }
}

/** PC-24 — die eigenen Baustellenberichte, für den Monteur-Download in „Berichte". */
export async function pcGetMyReports(): Promise<PcMeinBericht[]> {
  const r = await apiFetch<{ ok: boolean; berichte: PcMeinBericht[] }>("/pc/my-reports", {}, false);
  return r.berichte ?? [];
}

/**
 * PC-14 — Vollkostensaetze. Nur Geschaeftsfuehrung; die Pruefung sitzt
 * serverseitig, weil der JWT keine Rolle traegt und ein Gate allein im
 * Frontend niemanden aufhaelt.
 */
export async function pcGetCostRates(): Promise<{ mitarbeiter: PcEmployeeRates[]; ohne_satz: string[] }> {
  const r = await apiFetch<{ ok: boolean; mitarbeiter: PcEmployeeRates[]; ohne_satz: string[] }>(
    "/pc/cost-rates", {}, false
  );
  return { mitarbeiter: r.mitarbeiter ?? [], ohne_satz: r.ohne_satz ?? [] };
}

/**
 * Gleiches `gueltig_ab` korrigiert den bestehenden Satz, ein neues Datum legt
 * einen weiteren an. Altprojekte rechnen ueber das Datum weiter mit ihrem
 * damaligen Wert — deshalb wird hier nie ein Satz ueberschrieben.
 */
/**
 * PC-21 — Reihenfolge der Mannschaft speichern.
 *
 * Geschickt wird die Reihenfolge, nicht die Zahlen: der Server vergibt die
 * Positionen selbst. Sonst haengt die Sortierung davon ab, ob die Oberflaeche
 * sauber durchnummeriert hat.
 *
 * Betrifft immer nur die Rollen, die in der Liste vorkommen — wer Monteure
 * umsortiert, ruehrt die Helfer nicht an.
 */
export async function pcSaveReihenfolge(employeeIds: string[]): Promise<{
  ok: boolean; gesetzt: number;
}> {
  return apiFetch("/pc/reihenfolge", {
    method: "POST",
    body: JSON.stringify({ reihenfolge: employeeIds.map((id) => ({ employee_id: id })) }),
  }, false);
}

export async function pcSaveCostRate(input: {
  employee_id: string; vks: number; gueltig_ab: string; notiz?: string;
}): Promise<{ ok: boolean; satz: { id: string; vks: string; gueltig_ab: string } }> {
  return apiFetch("/pc/cost-rate", { method: "POST", body: JSON.stringify(input) }, false);
}

/** PC-6 — Übersicht. Liefert nur Projekte, mit denen etwas passiert ist. */
export async function pcGetControllingProjekte(): Promise<{
  projekte: PcControllingProjekt[];
  summe: Record<string, number>;
}> {
  const r = await apiFetch<{ ok: boolean; projekte: PcControllingProjekt[];
                             summe: Record<string, number> }>("/pc/projects", {}, false);
  return { projekte: r.projekte ?? [], summe: r.summe ?? {} };
}

/** PC-6b — alles zu einem Projekt in einem Aufruf. */
export async function pcGetProjektDetail(id: string): Promise<PcProjektDetail> {
  return apiFetch<PcProjektDetail>(`/pc/project?id=${encodeURIComponent(id)}`, {}, false);
}

/**
 * PC-11 — Kalkulation speichern. Das Soll wird beim ersten Eintrag eingefroren;
 * ein späterer Änderungsversuch kommt als Warnung zurück, nicht als stiller
 * Fehlschlag. Deshalb `warnungen` immer anzeigen.
 */
/**
 * PC-17 — Nachtraege anlegen, aendern, loeschen.
 *
 * Eigener Endpunkt und nicht Teil von `pcSaveKalkulation`: ein Nachtrag wird zu
 * einem anderen Zeitpunkt angefasst als die Kalkulation, und ein Aufruf, der nur
 * einen Nachtrag buchen wollte, hat schon einmal die ganze Stundenverteilung
 * mitgeloescht.
 *
 * Antwort ist immer der vollstaendige Stand des Projekts — die Seite muss danach
 * nichts nachladen. Bei Ablehnung kommt HTTP 422 mit `error` als Kennung
 * (`grund_fehlt`, `leerer_nachtrag`, `nachtrag_unbekannt`).
 */
/**
 * PC-18 — Angebotskalkulation anlegen, benennen, mit einer Projektakte verknuepfen.
 *
 * `verknuepfen` fuehrt zusammen: in pc.projects stehen bereits ueber 600 Akten
 * aus PDS, die passende existiert beim Zuschlag also meist schon. Alles, was an
 * der Angebotskalkulation haengt, wandert dann auf die Aktenzeile.
 *
 * Liegt auf der Akte schon eine Kalkulation, kommt HTTP 422 mit
 * `ziel_hat_kalkulation` — es wird nichts ueberschrieben.
 */
/**
 * PC-19 — beide Ranglisten und die Auswertung nach Auftragsart in einem Aufruf.
 *
 * Zusammen und nicht einzeln, damit die drei Listen garantiert denselben
 * Zeitraum sehen. Der Zeitraum filtert auf das Abschlussdatum; was noch laeuft,
 * bleibt sichtbar.
 */
/**
 * PC-20 — Vorgangsnummer in PDS suchen.
 *
 * PDS hat keine gemeinsame Vorgangssuche; der Endpunkt fragt Angebote,
 * Auftraege, Rechnungen und Serviceauftraege einzeln ab und legt die Treffer
 * zusammen.
 */
/**
 * PC-18 — Kalkulation loeschen.
 *
 * Entfernt Soll, Zahlen, Stundenverteilung und Nachtraege. Die Projektzeile
 * faellt nur bei einer reinen Angebotskalkulation ohne Berichte, Regiezeilen
 * und offene Punkte — Betriebsdokumentation bleibt in jedem Fall stehen.
 *
 * `projekt_geloescht` sagt, ob die Seite noch existiert; `grund` sagt, warum
 * die Zeile geblieben ist.
 */
export async function pcLoescheKalkulation(projectId: string): Promise<{
  ok: boolean;
  name: string;
  projekt_geloescht: boolean;
  grund: 'aus_pds' | 'hat_berichte' | 'hat_regie' | 'hat_offene_punkte' | null;
  berichte: number;
  offene_punkte: number;
  regiezeilen: number;
}> {
  return apiFetch("/pc/projekt", {
    method: "POST",
    body: JSON.stringify({ aktion: "loeschen", project_id: projectId }),
  }, false);
}

export async function pcSucheVorgang(nummer: string): Promise<{
  treffer: PcVorgangTreffer[];
}> {
  const r = await apiFetch<{ ok: boolean; treffer: PcVorgangTreffer[] }>(
    "/pc/pds-vorgang", { method: "POST", body: JSON.stringify({ aktion: "suchen", nummer }) }, false);
  return { treffer: r.treffer ?? [] };
}

/**
 * PC-20 — einen Vorgang lesen und auswerten.
 *
 * Liefert die gerechneten Summen UND die Positionsliste. Beides, weil die
 * Einordnung einer Position sich nicht zuverlaessig automatisieren laesst:
 * Bedarfspositionen zaehlen nicht zum Angebotsbetrag, und Posten mit Kosten
 * ohne Lohnzeit bedeuten meist fehlende Stunden im Soll.
 */
export async function pcLeseVorgang(uuid: string, typ: string): Promise<PcVorgangDaten> {
  return apiFetch<PcVorgangDaten>(
    "/pc/pds-vorgang", { method: "POST", body: JSON.stringify({ aktion: "lesen", uuid, typ }) }, false);
}

export async function pcGetRanking(von?: string, bis?: string): Promise<{
  angebote: PcRangZeile[];
  abrechnungen: PcRangZeile[];
  nach_art: PcRangArt[];
}> {
  const q = new URLSearchParams();
  if (von) q.set('von', von);
  if (bis) q.set('bis', bis);
  const pfad = `/pc/ranking${q.toString() ? `?${q}` : ''}`;
  const r = await apiFetch<{ ok: boolean; angebote: PcRangZeile[];
                            abrechnungen: PcRangZeile[]; nach_art: PcRangArt[] }>(pfad, {}, false);
  return { angebote: r.angebote ?? [], abrechnungen: r.abrechnungen ?? [],
           nach_art: r.nach_art ?? [] };
}

export async function pcSaveProjekt(input: {
  aktion: 'anlegen' | 'aendern' | 'verknuepfen';
  project_id?: string;
  projektakte_uuid?: string;
  name?: string;
  nummer?: string | null;
  auftragsart?: string | null;
}): Promise<{
  ok: boolean;
  meldung: string | null;
  projekt: {
    id: string; name: string; nummer: string | null;
    auftragsart: string | null; quelle: string; projektakte_uuid: string | null;
  };
  error?: string;
}> {
  return apiFetch("/pc/projekt", { method: "POST", body: JSON.stringify(input) }, false);
}

export async function pcSaveNachtrag(input: {
  aktion: 'anlegen' | 'aendern' | 'loeschen';
  id?: string;
  project_id?: string;
  stunden?: number;
  summe?: number;
  material_ek?: number;
  grund?: string;
}): Promise<{
  ok: boolean;
  id: string | null;
  nachtraege: PcNachtrag[];
  summen: { stunden: number; summe: number; material: number; anzahl: number };
  error?: string;
  meldung?: string;
}> {
  return apiFetch("/pc/nachtrag", { method: "POST", body: JSON.stringify(input) }, false);
}

export async function pcSaveKalkulation(payload: PcKalkulationPayload): Promise<{
  ok: boolean; soll_lohnminuten: number | null;
  zeilen_gespeichert: number; zeilen_entfernt: number;
  nachtrag_gebucht: boolean; warnungen: string[];
}> {
  return apiFetch("/pc/calculation", { method: "POST", body: JSON.stringify(payload) }, false);
}

// ─────────────────────────────────────────────────────────────────────
// Berechtigungen light (ZUGANG-1/2/3)
// ─────────────────────────────────────────────────────────────────────

/** Eigene Rechte. Jeder Angemeldete darf das abfragen — für sich selbst. */
export async function getMeineRechte(): Promise<MeineRechte> {
  return apiFetch<MeineRechte>("/zugang/meine", {}, false);
}

/** Die ganze Matrix. Nur für den, der die Einstellungen darf. */
export async function getZugangMatrix(): Promise<ZugangMatrix> {
  return apiFetch<ZugangMatrix>("/zugang/alle", {}, false);
}

/**
 * Matrix sichern. Der Server lehnt zwei Fälle ab, die man sonst erst merkt,
 * wenn es zu spät ist: sich selbst die Einstellungen zu entziehen, und
 * niemanden mehr übrig zu lassen, der sie hat.
 */
export async function saveZugangMatrix(
  matrix: Record<string, ZugangBereich[]>
): Promise<{ ok: boolean; personen: number }> {
  return apiFetch("/zugang/schreiben", { method: "POST", body: JSON.stringify({ matrix }) }, false);
}

// ── Feedback-Modul (PC-25/26) ────────────────────────────────────────
/**
 * PC-25 — Aufnahme auswerten. Whisper erkennt die Sprache selbst (Deutsch
 * oder Bosnisch im Betrieb) und die Extraktion übersetzt bei Bedarf — kein
 * Sprachparameter hier, der würde alles andere als Deutsch verstümmeln.
 */
export async function pcFeedbackExtract(audioBlob: Blob): Promise<PcFeedbackExtractResponse> {
  assertVoiceOnline();
  const formData = new FormData();
  formData.append("audio", audioBlob, "feedback.webm");

  const token = getToken();
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const response = await fetch(`${API_BASE}/pc/feedback/extract`, {
    method: "POST",
    headers,
    body: formData,
  });

  if (!response.ok) {
    throw new Error("Auswertung der Aufnahme fehlgeschlagen");
  }
  return response.json();
}

/**
 * PC-26 — die bestätigte Liste speichern. allowQueue=false (Bug 06.09.2026):
 * die generische Offline-Queue re-sendet automatisch bei Wiederverbindung,
 * aber pc.save_feedback() ist NICHT idempotent — legt bei jedem Aufruf neue
 * Zeilen an. Ohne Queue schlägt der Versuch bei fehlendem Netz sofort und
 * sichtbar fehl, die Einträge bleiben in der UI stehen (siehe Feedback.tsx
 * senden()) und der Mitarbeiter sendet bewusst manuell erneut — kein
 * automatischer Zweitversuch, der dieselben Einträge doppelt anlegen könnte.
 */
export async function pcFeedbackSave(payload: {
  transkript_roh: string;
  sprache_erkannt: string | null;
  eintraege: PcFeedbackEintrag[];
}): Promise<PcFeedbackSaveResponse> {
  return apiFetch<PcFeedbackSaveResponse>("/pc/feedback/save", {
    method: "POST",
    body: JSON.stringify(payload),
  }, false);
}

// ── Aufgabenliste (PC-27/28) ─────────────────────────────────────────
export async function pcGetMyTasks(): Promise<PcMeineAufgabe[]> {
  const r = await apiFetch<{ ok: boolean; aufgaben: PcMeineAufgabe[] }>("/pc/my-tasks", {}, false);
  return r.aufgaben ?? [];
}

export async function pcSetTaskStatus(id: string, status: 'offen' | 'erledigt'): Promise<{ ok: boolean }> {
  return apiFetch("/pc/task-status", { method: "POST", body: JSON.stringify({ id, status }) }, false);
}

/** PC-30 — projektgebundene Aufgaben über alle Projekte (nur Controlling-Zugriff). */
export async function pcGetProjectTasks(): Promise<PcProjektAufgabe[]> {
  const r = await apiFetch<{ ok: boolean; aufgaben: PcProjektAufgabe[] }>("/pc/project-tasks", {}, false);
  return r.aufgaben ?? [];
}

/**
 * PC-31 — Beobachtungen nach Thema, ueber alle Personen und Wochen.
 * Nur Fachbereich `gf`; alle anderen bekommen 403 (apiFetch wirft dann
 * Error("FORBIDDEN") mit .status === 403).
 * Ohne `von`/`bis` liefert der Server die letzten 90 Tage.
 */
export async function pcGetObservations(
  p: { von?: string; bis?: string; thema?: string; melder?: string } = {},
): Promise<PcBeobachtungen> {
  const q = new URLSearchParams();
  if (p.von) q.set('von', p.von);
  if (p.bis) q.set('bis', p.bis);
  if (p.thema) q.set('thema', p.thema);
  if (p.melder) q.set('melder', p.melder);
  const s = q.toString();
  return apiFetch<PcBeobachtungen>(`/pc/observations${s ? `?${s}` : ''}`, {}, false);
}

/**
 * PC-32 — Meldeuebersicht: wer hat an welchen Werktagen ueberhaupt gemeldet.
 * Nur Fachbereich `gf`, sonst 403. Ohne `von`/`bis` die letzten 30 Tage.
 */
export async function pcGetReportingStatus(
  p: { von?: string; bis?: string } = {},
): Promise<PcMeldeUebersicht> {
  const q = new URLSearchParams();
  if (p.von) q.set('von', p.von);
  if (p.bis) q.set('bis', p.bis);
  const s = q.toString();
  return apiFetch<PcMeldeUebersicht>(`/pc/reporting-status${s ? `?${s}` : ''}`, {}, false);
}

/** PC-29 — eigene Feedback-Aufnahmen zum Nachlesen (volles Rohtranskript). */
export async function pcGetMyFeedback(): Promise<PcFeedbackAufnahme[]> {
  const r = await apiFetch<{ ok: boolean; aufnahmen: PcFeedbackAufnahme[] }>("/pc/my-feedback", {}, false);
  return r.aufnahmen ?? [];
}


// ── Buerotaetigkeits-Dokumentation (PC-33/34/35) ─────────────────────
/**
 * PC-33 — Aufnahme auswerten. Wie pcFeedbackExtract: multipart mit dem Feld
 * `audio`, Whisper erkennt die Sprache selbst.
 *
 * 403 heisst hier immer dasselbe: der Bereich `buerobericht` fehlt in der
 * Berechtigungsmatrix. Der Fehler wird deshalb mit `status` weitergereicht,
 * damit die Seite den Weg zum Haken nennen kann statt "Auswertung
 * fehlgeschlagen" zu sagen -- der Unterschied zwischen "kaputt" und "noch
 * nicht freigeschaltet" ist fuer denjenigen vor dem Bildschirm der ganze.
 */
export async function pcBueroExtract(audioBlob: Blob): Promise<PcBueroExtractResponse> {
  assertVoiceOnline();
  const formData = new FormData();
  formData.append("audio", audioBlob, "buerobericht.webm");

  const token = getToken();
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const response = await fetch(`${API_BASE}/pc/office/extract`, {
    method: "POST",
    headers,
    body: formData,
  });

  if (!response.ok) {
    const err = new Error(
      response.status === 403
        ? "FORBIDDEN"
        : "Auswertung der Aufnahme fehlgeschlagen",
    ) as Error & { status?: number };
    err.status = response.status;
    throw err;
  }
  return response.json();
}

/**
 * PC-34 — die bestaetigte Liste speichern. allowQueue=false aus demselben
 * Grund wie bei pcFeedbackSave: pc.save_buero_bericht() ist NICHT idempotent,
 * ein automatischer Zweitversuch legte denselben Bericht doppelt an. Bei
 * fehlendem Netz schlaegt der Aufruf sichtbar fehl, die Liste bleibt stehen,
 * der Mensch drueckt bewusst noch einmal.
 *
 * `datum` ist optional: ohne Angabe nimmt die Datenbank heute. Ein Datum in
 * der Zukunft deckelt sie auf heute, ein zurueckliegendes ist ein normaler
 * Vorgang (Montag ueber den Freitag berichten).
 */
export async function pcBueroSave(payload: {
  datum?: string | null;
  transkript_roh: string;
  sprache_erkannt: string | null;
  eintraege: PcBueroEintrag[];
}): Promise<PcBueroSaveResponse> {
  return apiFetch<PcBueroSaveResponse>("/pc/office/save", {
    method: "POST",
    body: JSON.stringify(payload),
  }, false);
}

/**
 * PC-35 — Auswertung. OHNE `person` immer die eigenen Zahlen; das ist der
 * Normalfall und der Sinn der Sache (Eigensteuerung statt Fremdkontrolle).
 *
 * `person` ist nur fuer Fachbereich `gf` zulaessig -- sonst 403, bewusst
 * ohne stilles Zurueckfallen auf die eigenen Daten. Die Oberflaeche bietet
 * die Auswahl deshalb auch nur der GF an.
 */
export async function pcGetBueroReport(
  p: { von?: string; bis?: string; person?: string } = {},
): Promise<PcBueroReport> {
  const q = new URLSearchParams();
  if (p.von) q.set('von', p.von);
  if (p.bis) q.set('bis', p.bis);
  if (p.person) q.set('person', p.person);
  const s = q.toString();
  return apiFetch<PcBueroReport>(`/pc/office/report${s ? `?${s}` : ''}`, {}, false);
}
